// --- START: Conditional Logger ---
// To enable logging, add `?debug` to the URL. Off in prod by default.
// When off, the .log/.warn/.group/.groupEnd entries are literal no-ops
// (not wrapped if-checks) so V8 can inline + drop the call. Callers
// still evaluate their argument expressions — that's unavoidable in JS —
// but the function body adds zero extra work.
const NALA_DEBUG_MODE = (() => {
  try {
    const q = window.location.search || "";
    return /[?&]debug(?:=|&|$)/.test(q);
  } catch (_) { return false; }
})();
const NALA_DEBUG_START = performance.now();

const _noop = () => {};
const Logger = NALA_DEBUG_MODE
  ? {
      _ts: () => `+${((performance.now() - NALA_DEBUG_START) / 1000).toFixed(3)}s`,
      log: (...args) => console.log(`[${Logger._ts()}]`, ...args),
      warn: (...args) => console.warn(`[${Logger._ts()}]`, ...args),
      error: (...args) => console.error(`[${Logger._ts()}]`, ...args),
      group: (...args) => console.group(`[${Logger._ts()}]`, ...args),
      groupEnd: () => console.groupEnd(),
    }
  : {
      _ts: () => "",
      log: _noop,
      warn: _noop,
      // Errors always log even with debug off — pass straight through.
      error: (...args) => console.error(...args),
      group: _noop,
      groupEnd: _noop,
    };
// --- END: Conditional Logger ---

// --- START: Route URL constants ---
// Hoist the repeated URL strings built from `routes` into module-level
// constants computed once at load. ~15+ template-literal concats per
// cart op flatten to plain property reads, and the URLs become greppable
// in one place if Shopify ever ships a route shape change.
const _CART_URL =
  (typeof routes !== "undefined" && routes && routes.cart_url) || "/cart";
const _CART_ADD_URL =
  (typeof routes !== "undefined" && routes && routes.cart_add_url) ||
  "/cart/add";
const CART_JS_URL = `${_CART_URL}.js`;
// Full cart-drawer section + bubble — heavy server render (~500-1500ms).
// Used by refreshCartDrawerWithReinit / FreeGiftManager / pageshow restore
// where we genuinely need to repaint the entire drawer chrome.
const CART_DRAWER_SECTIONS_URL = `${_CART_URL}?sections=cart-drawer,cart-icon-bubble`;
// Items-only section + bubble — small render (~30-50ms). Used by the
// fast-add path and the surgical refresh, where chrome is updated
// client-side from /cart.js and only the items list needs server HTML.
const CART_ITEMS_SECTIONS_URL = `${_CART_URL}?sections=cart-drawer-items,cart-icon-bubble`;
// --- END: Route URL constants ---

// Cart Drawer Enhancements - Rebuy Features Replication
// Configurable settings at the top for easy editing

/**
 * CartWatcher - Observes cart changes using PerformanceObserver
 * Based on: https://mikefallows.com/posts/observing-cart-changes-in-a-shopify-theme/
 *
 * WHY THIS IS BETTER:
 * - Uses PerformanceObserver to detect cart API calls (no fetch interception needed)
 * - Works with any script/app that modifies the cart (not just our code)
 * - Compares old vs new cart state to detect actual changes (not just requests)
 * - Emits clean events that other code can listen to
 * - No race conditions from trying to intercept fetch at the right time
 *
 * HOW IT WORKS:
 * 1. Stores current cart state in localStorage
 * 2. Uses PerformanceObserver to detect any /cart/* requests
 * 3. After detecting a request, fetches fresh cart state
 * 4. Compares old vs new to find what actually changed
 * 5. Emits 'cart_changed' event with oldCart, newCart, and changes
 */
class CartWatcher {
  constructor() {
    this.storageKey = "cart_watcher_last_cart";
    this.cartObserver = null;
    this.lastTriggeringRequest = null;
    this.requestHistory = [];
    this.MAX_HISTORY = 10;
  }

  init() {
    // Initialize with current cart state. Returns the boot cart so
    // CartDrawerEnhancements.setup() can reuse it instead of firing
    // a second /cart.js on page load.
    //
    // Fast path 1 — empty cart: if the Liquid-rendered cart-icon-bubble
    // shows 0 items, synthesise an empty-cart object and skip the fetch.
    if (this._isLiquidCartEmpty()) {
      const emptyCart = this._buildEmptyCart();
      this.storeCart(emptyCart);
      this.observeCartChanges();
      return Promise.resolve(emptyCart);
    }

    // Fast path 2 — non-empty cart with Liquid state available: the
    // #cart-drawer-state JSON tag carries item_count / total_price /
    // currency from server-render. Build a stub cart from it so the
    // progress bar + item counter + bubble can render immediately
    // without waiting on /cart.js. Gift-management code (which needs
    // real items[]) defers to a requestIdleCallback fetch — see
    // CartDrawerEnhancements.fetchAndUpdateCart.
    //
    // Saves ~150-300ms on every PDP visit for returning customers.
    const stub = this._buildStubFromLiquidState();
    if (stub) {
      this.storeCart(stub);
      this.observeCartChanges();
      return Promise.resolve(stub);
    }

    return this.emitCartChanges().then((cart) => {
      this.observeCartChanges();
      return cart;
    });
  }

  /**
   * Build a minimal cart object from the inline #cart-drawer-state
   * JSON tag. items[] is left empty — callers that need real items
   * (gift managers, change detection) must fetch /cart.js explicitly
   * and the `isStub` flag tells emitCartChanges to skip the diff
   * dispatch on the first replacement.
   */
  _buildStubFromLiquidState() {
    const el = document.getElementById("cart-drawer-state");
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      if (typeof data.item_count !== "number") return null;
      return {
        items: [],
        item_count: data.item_count,
        total_price: typeof data.total_price === "number" ? data.total_price : 0,
        total_discount: typeof data.total_discount === "number" ? data.total_discount : 0,
        currency:
          data.currency ||
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          "AUD",
        isStub: true,
      };
    } catch (_) {
      return null;
    }
  }

  _isLiquidCartEmpty() {
    // Bubble is hidden entirely (display:none via .cart-count-bubble:empty)
    // when item_count == 0 in Dawn-style themes. Even if not, a visible
    // bubble with text "0" still means empty.
    const bubble = document.querySelector("#cart-icon-bubble .cart-count-bubble span:not(.visually-hidden)");
    if (!bubble) {
      // No bubble visible — treat as empty.
      return true;
    }
    const n = parseInt(bubble.textContent.trim(), 10);
    return n === 0;
  }

  _buildEmptyCart() {
    // Match Shopify /cart.js shape for the fields downstream code reads.
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "AUD";
    return { items: [], item_count: 0, total_price: 0, currency };
  }

  async fetchCart() {
    const fetchFn = window._originalFetch || window.fetch;
    const response = await fetchFn(CART_JS_URL);
    if (!response.ok) {
      Logger.error("[CartWatcher] Failed to fetch cart");
      return null;
    }
    return await response.json();
  }

  storeCart(cart) {
    if (!cart) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cart));
    } catch (e) {
      Logger.warn("[CartWatcher] Failed to store cart:", e);
    }
  }

  getStoredCart() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : { items: [] };
    } catch (e) {
      Logger.warn("[CartWatcher] Failed to get stored cart:", e);
      return { items: [] };
    }
  }

  findCartChanges(oldCart, newCart) {
    if (!oldCart || !oldCart.items) oldCart = { items: [] };
    if (!newCart || !newCart.items) newCart = { items: [] };

    const onlyInLeft = (l, r) =>
      l.filter((li) => !r.some((ri) => li.key === ri.key));

    let result = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
    };

    // Check for quantity changes in existing items
    oldCart.items.forEach((oi) => {
      const ni = newCart.items.find(
        (i) => i.key === oi.key && i.quantity !== oi.quantity,
      );
      if (!ni) return;

      let quantity = ni.quantity - oi.quantity;
      let item = { ...ni };
      item.quantity = Math.abs(quantity);

      if (quantity > 0) {
        result.added.push(item);
      } else {
        result.removed.push(item);
      }
    });

    return result;
  }

  async emitCartChanges(silent = false) {
    const newCart = await this.fetchCart();
    if (!newCart) return;

    const oldCart = this.getStoredCart();
    // Boot-stub guard: when init() seeded storage from the Liquid
    // #cart-drawer-state tag, items[] is empty by construction. Diffing
    // against an empty stub would mark every real item as "added" and
    // emit a spurious cart_changed. Skip the diff for this first call;
    // storage replaces the stub with the real cart below.
    const oldWasStub = !!(oldCart && oldCart.isStub);
    const changes = oldWasStub
      ? { added: [], removed: [] }
      : this.findCartChanges(oldCart, newCart);

    // Only emit event if there are actual changes
    if (!silent && !oldWasStub && (changes.added.length > 0 || changes.removed.length > 0)) {
      Logger.log("[CartWatcher] Change detected:", {
        added: changes.added.length,
        removed: changes.removed.length,
        itemCount: newCart.item_count,
      });

      // Check if free gift was removed (for debugging)
      const freeGiftRemoved = changes.removed.find(
        (item) => item.properties?._free_gift === "true",
      );

      if (freeGiftRemoved) {
        Logger.warn("[CartWatcher] Free gift removed");
      }

      const event = new CustomEvent("cart_changed", {
        detail: {
          oldCart,
          newCart,
          changes,
          triggeringRequest: this.lastTriggeringRequest,
        },
      });
      window.dispatchEvent(event);

      this.lastTriggeringRequest = null;
    } else if (silent) {
      // Internal /cart/add — product-form already rendered the drawer
      // and ran onCartUpdate via the cart:updated event. We still need
      // to refresh stored state so a subsequent external mutation
      // diffs against the correct items array.
      this.lastTriggeringRequest = null;
    }

    this.storeCart(newCart);
    return newCart;
  }

  observeCartChanges() {
    // Check if PerformanceObserver is supported
    if (typeof PerformanceObserver === "undefined") {
      Logger.warn(
        "[CartWatcher] PerformanceObserver not supported, falling back to polling",
      );
      // Fallback: poll every 2 seconds (only if needed)
      setInterval(() => this.emitCartChanges(), 2000);
      return;
    }

    this.cartObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const name = entry.name;
        // Cheap short-circuit FIRST — analytics beacons, fonts, images,
        // third-party scripts (Visually.io, Klaviyo, Shopify pixels) all
        // fire as resource entries. Skipping non-cart URLs with a single
        // indexOf avoids the regex + downstream work on hundreds of
        // entries per page load.
        if (name.indexOf("/cart") === -1) continue;
        if (
          entry.initiatorType !== "xmlhttprequest" &&
          entry.initiatorType !== "fetch"
        ) {
          continue;
        }
        // /cart/foo guard (so /cart.js still matches further down but
        // /not-cart/whatever doesn't slip through).
        if (!/\/cart(\/|\.js|\?|$)/.test(name)) continue;

        const isUpdateRequest = name.indexOf("/cart/update") !== -1;
        const isAddRequest = name.indexOf("/cart/add") !== -1;
        const isChangeRequest = name.indexOf("/cart/change") !== -1;
        const isReadRequest = name.indexOf("/cart.js") !== -1;
        // /cart.js (reads) and /cart?sections= (also reads) are non-
        // modifying — skip emitCartChanges for those. Pre-compute once.
        const isModifying = !isReadRequest && name.indexOf("sections=") === -1;

        const requestInfo = {
          url: name,
          timestamp: Date.now(),
          duration: entry.duration,
          initiatorType: entry.initiatorType,
          startTime: entry.startTime,
          isModifying,
          requestType: isUpdateRequest
            ? "UPDATE (often bundle apps)"
            : isAddRequest
              ? "ADD"
              : isChangeRequest
                ? "CHANGE (quantity/remove)"
                : isReadRequest
                  ? "READ"
                  : "UNKNOWN",
        };

        // Add to history
        this.requestHistory.push(requestInfo);
        if (this.requestHistory.length > this.MAX_HISTORY) {
          this.requestHistory.shift();
        }

        // Only process modifying requests
        if (isModifying) {
          this.lastTriggeringRequest = requestInfo;

          // beginAdd() sets skipNextAdd before product-form fires
          // /cart/add. updateState (via the cart:updated event from
          // renderContents) has already updated the UI from the
          // bundled section response, so emitting cart_changed here
          // just causes a redundant onCartUpdate ~1s later. Still
          // refresh stored cart so external mutations diff correctly.
          const silent = this.skipNextAdd && isAddRequest;
          if (silent) this.skipNextAdd = false;

          Logger.log(
            "[CartWatcher] Cart request:",
            requestInfo.requestType,
            silent ? "(silent — internal add)" : "",
          );

          // Short delay to let the request complete
          setTimeout(() => {
            this.emitCartChanges(silent);
          }, 30);
        }
      }
    });

    try {
      this.cartObserver.observe({ entryTypes: ["resource"] });
      Logger.log("[CartWatcher] Started observing cart changes");
    } catch (e) {
      Logger.error("[CartWatcher] Failed to observe:", e);
    }
  }

  destroy() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
  }
}

// =============================================================================
// HOW TO CHANGE THE FREE GIFT
// =============================================================================
// 1. Go to the product URL and append .json, e.g.:
//      https://wearnala.com/products/your-product-handle.json
// 2. In the JSON, find "variants" and copy the "id" of the variant you want:
//      "variants": [{ "id": 51138141126889, ... }]
// 3. Paste it into FREE_GIFT_VARIANT_ID below.
// 4. Update the name everywhere it says "Mynd Subscription" in this config
//    (TIER_1_LABEL, TIER_1_MESSAGE, TIER_1_COMPLETE for both AUD and NZD).
// 5. Save. No other code changes needed — the gift auto-adds/removes itself.
//
// RECOMMENDED: Before pushing live, duplicate the theme in Shopify Admin
// (Online Store → Themes → ··· → Duplicate), make your changes there, and
// preview it using the "Preview" link. Only publish once you've confirmed the
// gift adds/removes correctly in the cart drawer.
//
// NOTE: The gift is added to ANY cart that has at least one regular item
// (TIER_1 threshold is $0). If you ever want a minimum spend, set TIER_1 to
// the amount in cents (e.g. 5000 = $50) for both AUD and NZD below.
// =============================================================================

const DEFAULT_CART_ENHANCEMENT_CONFIG = {
  // GWP master switch. When false, both the free gift and mystery gift are
  // PAUSED: managers never add a gift and will remove any gift already in the
  // cart. Re-enabled 2026-06-11 — scrunchie gift unlocked at $180 (TIER_2).
  GWP_ENABLED: true,

  // Free gift variant ID - this is what gets added to cart
  FREE_GIFT_VARIANT_ID: "51439545254121", // Free Gem Shaving Kits ($180 GWP)

  // Mystery gift variant ID — hardcoded to skip the product fetch
  MYSTERY_GIFT_VARIANT_ID: "48271391195369",

  // Mystery gift eligibility - URL parameters that trigger eligibility
  MYSTERY_GIFT_URL_TRIGGERS: ["gap flow", "gap+flow"],

  // Mystery gift cookie name and duration (24 hours)
  MYSTERY_GIFT_COOKIE_NAME: "mystery_gift",
  MYSTERY_GIFT_COOKIE_DURATION_HOURS: 24,

  // Timer duration in minutes
  TIMER_DURATION_MINUTES: 10,

  // Gift is unlocked at TIER_2 ($180 minimum spend)
  GIFT_TIER: "TIER_2",

  // Tier thresholds in cents (100 = $1)
  TIERS: {
    AUD: {
      TIER_1: 15000, // $150 - Free Express Shipping
      TIER_2: 18000, // $180 - Free Gem Shaving Kits (gift)
      TIER_1_LABEL: "Free Express Shipping",
      TIER_2_LABEL: "Free Gem Shaving Kits",
      TIER_1_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Express Shipping!`,
      TIER_2_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Your Free Gem Shaving Kits!`,
      TIER_1_COMPLETE: "You've Unlocked Free Express Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Your Free Gem Shaving Kits!",
    },
    NZD: {
      TIER_1: 15000, // $150 - Free Shipping
      TIER_2: 18000, // $160 - Free Gem Shaving Kits (gift)
      TIER_1_LABEL: "Free Shipping",
      TIER_2_LABEL: "Free Gem Shaving Kits",
      TIER_1_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Shipping!`,
      TIER_2_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Your Free Gem Shaving Kits!`,
      TIER_1_COMPLETE: "You've Unlocked Free Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Your Free Gem Shaving Kits!",
    },
  },

  // Supported currencies for progress bar (only show for these)
  SUPPORTED_CURRENCIES: ["AUD", "NZD"],
};

// Initialize global config if it doesn't exist
window.CART_ENHANCEMENT_CONFIG = window.CART_ENHANCEMENT_CONFIG || {};

// Merge logic for TIERS (preserve defaults if missing in override)
const _mergedTiers = { ...DEFAULT_CART_ENHANCEMENT_CONFIG.TIERS };
if (window.CART_ENHANCEMENT_CONFIG.TIERS) {
  for (const currency in window.CART_ENHANCEMENT_CONFIG.TIERS) {
    if (_mergedTiers[currency]) {
      _mergedTiers[currency] = {
        ..._mergedTiers[currency],
        ...window.CART_ENHANCEMENT_CONFIG.TIERS[currency],
      };
    } else {
      _mergedTiers[currency] = window.CART_ENHANCEMENT_CONFIG.TIERS[currency];
    }
  }
}

// Apply everything to the global object
// We use Object.assign to update the existing global object reference
// instead of replacing it, which preserves references if other scripts captured it.
Object.assign(window.CART_ENHANCEMENT_CONFIG, {
  ...DEFAULT_CART_ENHANCEMENT_CONFIG,
  ...window.CART_ENHANCEMENT_CONFIG,
  TIERS: _mergedTiers,
});

// Local reference for internal use (same pointer as global)
const CART_ENHANCEMENT_CONFIG = window.CART_ENHANCEMENT_CONFIG;

// Debug: Log final config to verify A/B test is applied correctly
Logger.log("[Config] Final config:", {
  GWP_ENABLED: CART_ENHANCEMENT_CONFIG.GWP_ENABLED,
  GIFT_TIER: CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2 (default)",
  FREE_GIFT_VARIANT_ID: CART_ENHANCEMENT_CONFIG.FREE_GIFT_VARIANT_ID,
  AUD_TIER_1: CART_ENHANCEMENT_CONFIG.TIERS?.AUD?.TIER_1 / 100,
  AUD_TIER_2: CART_ENHANCEMENT_CONFIG.TIERS?.AUD?.TIER_2 / 100,
  hasIcons: !!CART_ENHANCEMENT_CONFIG.ICONS,
});

// Helper function to get color swatch URL
function getColorSwatchURL(variantName) {
  if (!variantName) return null;
  const baseurl = "https://cdn.shopify.com/s/files/1/0651/7083/3641/files/";
  let sanitizedVariantName = variantName
    .replace(/['&®]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
  sanitizedVariantName = sanitizedVariantName.replace(/\//g, "-");
  return `${baseurl}${sanitizedVariantName}.jpg`;
}

// Timer Management Class - Global timer that runs continuously
class CartTimer {
  constructor() {
    this.intervalId = null;
    this.STORAGE_KEY = "cart_drawer_timer_end";
    this.DURATION = CART_ENHANCEMENT_CONFIG.TIMER_DURATION_MINUTES * 60 * 1000;
    this.endTime = null;
    this.isRunning = false;
  }

  init() {
    // If already running, don't restart
    if (this.isRunning && this.intervalId) {
      return;
    }

    // Get or set end time
    const storedEndTime = this.getEndTime();
    if (storedEndTime && storedEndTime > Date.now()) {
      this.endTime = storedEndTime;
    } else {
      this.endTime = Date.now() + this.DURATION;
      localStorage.setItem(this.STORAGE_KEY, this.endTime.toString());
    }

    // Start the countdown
    this.start();
  }

  getEndTime() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  }

  start() {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.isRunning = true;
    const self = this;

    // Update function
    const tick = function () {
      const minutesEl = document.getElementById("cart-timer-minutes");
      const secondsEl = document.getElementById("cart-timer-seconds");

      if (!minutesEl || !secondsEl) {
        return;
      }

      const now = Date.now();
      let remaining = Math.max(0, self.endTime - now);

      if (remaining <= 0) {
        // Timer expired - reset
        self.endTime = Date.now() + self.DURATION;
        localStorage.setItem(self.STORAGE_KEY, self.endTime.toString());
        remaining = self.DURATION;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      minutesEl.textContent = minutes.toString().padStart(2, "0");
      secondsEl.textContent = seconds.toString().padStart(2, "0");
    };

    // Run immediately
    tick();

    // Then run every second
    this.intervalId = setInterval(tick, 1000);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  destroy() {
    this.stop();
  }
}

// Progress Bar Management Class
class CartProgressBar {
  constructor() {
    // State tracking
    this.state = {
      percentage: 0,
      tier1Complete: false,
      tier2Complete: false,
      promptText: "",
      isVisible: false,
      currency: null,
    };

    // DOM element cache (populated on first use or after DOM change)
    this.elements = null;
    this.elementsVersion = 0;

    // Track the last fill DOM node we wrote to. If a subsequent update
    // sees a different node, the section was just swapped wholesale by
    // a renderContents / cart.js section refresh — snap to new state
    // instead of animating (otherwise the freshly-rendered Liquid fill
    // visibly animates from 0% upward).
    this._lastAppliedFillNode = null;

    // Debounce timer for rapid updates
    this.updateDebounceTimer = null;

    // Setup MutationObserver to detect DOM changes and re-apply state
    this.setupDOMObserver();
  }

  setupDOMObserver() {
    // Observe the cart drawer for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check if progress bar elements were replaced
      const progressFill = document.getElementById("cart-progress-bar-fill");
      if (progressFill && this.state.percentage > 0) {
        // Check if the fill width doesn't match our state
        const currentWidth = parseFloat(progressFill.style.width) || 0;
        if (Math.abs(currentWidth - this.state.percentage) > 1) {
          // DOM was replaced, re-apply state immediately without transition
          this.applyStateToDOM(true);
        }
      }
    });

    // Start observing when cart drawer exists
    const startObserving = () => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        observer.observe(cartDrawer, {
          childList: true,
          subtree: true,
        });
      } else {
        // Retry after a short delay
        setTimeout(startObserving, 100);
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", startObserving);
    } else {
      startObserving();
    }
  }

  /**
   * Get or cache DOM elements. Returns null if critical elements missing.
   * Uses version tracking to detect when elements need re-querying.
   */
  getElements(forceRefresh = false) {
    // Check if we need to refresh (forced or elements missing)
    if (
      forceRefresh ||
      !this.elements ||
      !document.body.contains(this.elements.section)
    ) {
      const section = document.getElementById("cart-drawer-progress-section");

      if (!section) {
        this.elements = null;
        return null;
      }

      this.elements = {
        section,
        fill: document.getElementById("cart-progress-bar-fill"),
        prompt: document.getElementById("cart-progress-bar-prompt"),
        meter: document.getElementById("cart-progress-bar-meter"),
        step1: document.getElementById("cart-progress-step-1"),
        step2: document.getElementById("cart-progress-step-2"),
        label1: document.getElementById("cart-progress-label-1"),
        label2: document.getElementById("cart-progress-label-2"),
        price1: document.getElementById("cart-progress-price-1"),
        price2: document.getElementById("cart-progress-price-2"),
        limitedSupply: document.getElementById("cart-progress-limited-supply"),
        timerHeader: document.getElementById("cart-drawer-timer-header"),
      };

      this.elementsVersion++;

      // Apply current state to fresh elements immediately (prevents flash)
      this.applyStateToDOM(true);
    }

    return this.elements;
  }

  /**
   * Force re-query of DOM elements (call after cart drawer HTML is replaced)
   */
  reinitElements() {
    this.getElements(true);
  }

  /**
   * Apply current state to DOM elements
   * @param {boolean} immediate - If true, skip transition for instant update
   */
  applyStateToDOM(immediate = false) {
    // Re-query elements directly to ensure we have fresh references
    const fill = document.getElementById("cart-progress-bar-fill");
    const step1 = document.getElementById("cart-progress-step-1");
    const step2 = document.getElementById("cart-progress-step-2");
    const prompt = document.getElementById("cart-progress-bar-prompt");
    const meter = document.getElementById("cart-progress-bar-meter");
    const section = document.getElementById("cart-drawer-progress-section");
    const timerHeader = document.getElementById("cart-drawer-timer-header");

    // Update visibility via CSS class (animatable opacity/max-height
    // transition driven from cart-drawer.liquid). The Liquid template
    // also emits .is-cart-empty when cart is empty, so the section
    // starts in the correct state on a fresh page render.
    if (section) {
      section.classList.toggle("is-cart-empty", !this.state.isVisible);
    }
    if (timerHeader) {
      timerHeader.classList.toggle("is-cart-empty", !this.state.isVisible);
    }

    if (!this.state.isVisible) {
      if (section) section.classList.remove("is-updating");
      return;
    }

    // Update icons if overridden in config
    if (CART_ENHANCEMENT_CONFIG.ICONS) {
      if (step1 && CART_ENHANCEMENT_CONFIG.ICONS.TIER_1) {
        step1.innerHTML = CART_ENHANCEMENT_CONFIG.ICONS.TIER_1;
      }
      if (step2 && CART_ENHANCEMENT_CONFIG.ICONS.TIER_2) {
        step2.innerHTML = CART_ENHANCEMENT_CONFIG.ICONS.TIER_2;
      }
    }

    // Update labels, prices, and limited supply tag to match current tier configuration
    const label1 = document.getElementById("cart-progress-label-1");
    const label2 = document.getElementById("cart-progress-label-2");
    const price1El = document.getElementById("cart-progress-price-1");
    const price2El = document.getElementById("cart-progress-price-2");
    const limitedSupplyEl = document.getElementById(
      "cart-progress-limited-supply",
    );
    if (
      this.state.currency &&
      CART_ENHANCEMENT_CONFIG.TIERS[this.state.currency]
    ) {
      const tiers = CART_ENHANCEMENT_CONFIG.TIERS[this.state.currency];
      if (label1) label1.textContent = tiers.TIER_1_LABEL;
      if (label2) label2.textContent = tiers.TIER_2_LABEL;
      if (price1El)
        price1El.textContent = tiers.TIER_1 === 0 ? "" : `$${(tiers.TIER_1 / 100).toFixed(0)}`;
      if (price2El)
        price2El.textContent = `$${(tiers.TIER_2 / 100).toFixed(0)}`;
      // Position limited supply tag under the gift tier
      if (limitedSupplyEl) {
        const giftTier = CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2";
        const targetGroupId =
          giftTier === "TIER_1"
            ? "cart-progress-label-group-1"
            : "cart-progress-label-group-2";
        const targetGroup = document.getElementById(targetGroupId);
        if (targetGroup && limitedSupplyEl.parentElement !== targetGroup) {
          targetGroup.appendChild(limitedSupplyEl);
        }
      }
      this.updateAnnouncementBar(this.state.currency);
    }

    // Update progress bar fill
    if (fill) {
      if (immediate) {
        fill.style.transition = "none";
        fill.style.width = `${this.state.percentage}%`;
        // Force reflow then restore transition
        fill.offsetHeight;
        // Restore transition after a micro-task
        requestAnimationFrame(() => {
          fill.style.transition = "width 0.3s ease";
        });
      } else {
        fill.style.transition = "width 0.3s ease";
        fill.style.width = `${this.state.percentage}%`;
      }
    }

    // Update tier icons
    if (step1) {
      step1.classList.toggle("complete", this.state.tier1Complete);
    }
    if (step2) {
      step2.classList.toggle("complete", this.state.tier2Complete);
    }

    // Update prompt text
    if (prompt) {
      prompt.textContent = this.state.promptText;
    }

    // Update ARIA
    if (meter) {
      meter.setAttribute("aria-valuenow", Math.round(this.state.percentage));
    }
  }

  /**
   * Calculate cart total excluding free gift items
   */
  calculateCartTotal(cart) {
    if (!cart || !cart.items) return 0;

    let total = cart.total_price;

    for (const item of cart.items) {
      // Exclude any item with _free_gift property
      if (item.properties && item.properties._free_gift === "true") {
        total -= item.price * item.quantity;
      }
    }

    return Math.max(0, total);
  }

  /**
   * Calculate progress state from cart data
   * Returns new state object without modifying DOM
   */
  calculateState(cart) {
    // Defensive: malformed cart input (no numeric item_count, e.g. the
    // raw /cart/add.js line-item response) used to flip isVisible to
    // false and hide the bar between section-swap and CartWatcher's
    // refresh. Keep the existing state instead so update() short-circuits.
    if (cart && typeof cart.item_count !== "number") {
      return { ...this.state };
    }

    const currency = cart?.currency || "AUD";
    const isSupported =
      CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency);
    const hasItems = cart && cart.item_count > 0;

    // Determine visibility
    const isVisible = isSupported && hasItems;

    if (!isVisible) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: "",
        isVisible: false,
        currency,
      };
    }

    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: "",
        isVisible: false,
        currency,
      };
    }

    const cartTotal = this.calculateCartTotal(cart);

    let percentage = 0;
    let promptText = "";
    let tier1Complete = false;
    let tier2Complete = false;

    if (cartTotal < tiers.TIER_1) {
      // Before tier 1: Progress 0% to 25% (icon 1 at 25%)
      const remaining = tiers.TIER_1 - cartTotal;
      percentage = (cartTotal / tiers.TIER_1) * 25;
      promptText = tiers.TIER_1_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = false;
      tier2Complete = false;
    } else if (cartTotal < tiers.TIER_2) {
      // Between tier 1 and tier 2: Progress 25% to 75% (icon 2 at 75%)
      const remaining = tiers.TIER_2 - cartTotal;
      const progressInTier2 =
        (cartTotal - tiers.TIER_1) / (tiers.TIER_2 - tiers.TIER_1);
      percentage = 25 + progressInTier2 * 50;
      promptText = tiers.TIER_2_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = true;
      tier2Complete = false;
    } else {
      // Reached tier 2: Fill to 100%
      percentage = 100;
      promptText = tiers.TIER_2_COMPLETE;
      tier1Complete = true;
      tier2Complete = true;
    }

    return {
      percentage: Math.min(100, Math.max(0, percentage)),
      tier1Complete,
      tier2Complete,
      promptText,
      isVisible: true,
      currency,
    };
  }

  /**
   * Update tier labels based on currency
   */
  updateLabels(currency) {
    const el = this.elements;
    if (!el) return;

    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) return;

    if (el.label1) el.label1.textContent = tiers.TIER_1_LABEL;
    if (el.label2) el.label2.textContent = tiers.TIER_2_LABEL;
    if (el.price1)
      el.price1.textContent = tiers.TIER_1 === 0 ? "" : `$${(tiers.TIER_1 / 100).toFixed(0)}`;
    if (el.price2)
      el.price2.textContent = `$${(tiers.TIER_2 / 100).toFixed(0)}`;
  }

  /**
   * Update announcement bar messages from config (so Variant A $200 vs Variant B $130 is correct)
   */
  updateAnnouncementBar(currency) {
    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) return;

    const bar = document.getElementById("cart-drawer-announcement-bar");
    if (!bar) return;

    const messages = bar.querySelectorAll(".announcement-message");
    const t2 = (tiers.TIER_2 / 100).toFixed(0);
    if (messages[0])
      messages[0].textContent = tiers.TIER_1 === 0
        ? tiers.TIER_1_LABEL.toUpperCase()
        : `${tiers.TIER_1_LABEL.toUpperCase()} OVER $${(tiers.TIER_1 / 100).toFixed(0)}`;
    if (messages[1])
      messages[1].textContent = `${tiers.TIER_2_LABEL.toUpperCase()} OVER $${t2}`;
  }

  /**
   * Update visibility of progress section and timer header via the
   * same .is-cart-empty class the Liquid template uses. Querying live
   * each time to survive section innerHTML swaps that may have
   * detached the cached references.
   */
  updateVisibility(isVisible) {
    const section = document.getElementById("cart-drawer-progress-section");
    const timerHeader = document.getElementById("cart-drawer-timer-header");
    if (section) section.classList.toggle("is-cart-empty", !isVisible);
    if (timerHeader) timerHeader.classList.toggle("is-cart-empty", !isVisible);
  }

  /**
   * Main update method - calculates state and updates DOM
   * @param {Object} cart - Shopify cart object
   * @param {boolean} forceRefresh - Force DOM element refresh
   */
  update(cart, forceRefresh = false) {
    // Get/refresh elements
    const el = this.getElements(forceRefresh);

    if (!el) {
      Logger.warn("CartProgressBar: Progress section not found in DOM");
      return;
    }

    // Calculate new state
    const newState = this.calculateState(cart);

    // Check if state actually changed (avoid unnecessary DOM updates)
    const stateChanged =
      this.state.percentage !== newState.percentage ||
      this.state.tier1Complete !== newState.tier1Complete ||
      this.state.tier2Complete !== newState.tier2Complete ||
      this.state.promptText !== newState.promptText ||
      this.state.isVisible !== newState.isVisible ||
      this.state.currency !== newState.currency;

    if (!stateChanged && !forceRefresh) {
      return; // No changes, skip DOM updates
    }

    // Store new state
    this.state = newState;

    // Update visibility
    this.updateVisibility(newState.isVisible);

    if (!newState.isVisible) {
      this._lastAppliedFillNode = null; // next show will snap, not animate
      return;
    }

    // Update labels if currency changed
    if (this.state.currency !== newState.currency || forceRefresh) {
      this.updateLabels(newState.currency);
    }

    // Decide snap vs animate based on whether the fill DOM node is the
    // same one we wrote to last time. A different node means the
    // section was just swapped wholesale (renderContents, free-gift
    // refresh, cart.js quantity render) — animating from the freshly-
    // rendered Liquid 0% looks like a "restart from zero" bug.
    const fill = document.getElementById("cart-progress-bar-fill");
    const isFreshDom = this._lastAppliedFillNode !== fill;

    this.applyStateToDOM(isFreshDom);
    this._lastAppliedFillNode = fill;
  }
}

// Apply cart-drawer and cart-icon-bubble sections to the DOM in-place.
// Shared by FreeGiftManager and MysteryGiftManager after sections-API mutations.
function updateCartDrawerFromSections(sections) {
  if (!sections) return;

  const cartDrawer = document.querySelector("cart-drawer");

  if (sections["cart-drawer"] && cartDrawer) {
    const wasActive = cartDrawer.classList.contains("active");
    const scrollTop =
      cartDrawer.querySelector(".drawer__inner")?.scrollTop || 0;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sections["cart-drawer"];

    const newDrawerContent = tempDiv.querySelector("cart-drawer");
    if (newDrawerContent) {
      cartDrawer.innerHTML = newDrawerContent.innerHTML;
    } else {
      const sectionContent = tempDiv.querySelector(".shopify-section");
      if (sectionContent) {
        const innerDrawer = sectionContent.querySelector("cart-drawer");
        if (innerDrawer) {
          cartDrawer.innerHTML = innerDrawer.innerHTML;
        }
      }
    }

    if (wasActive) {
      cartDrawer.classList.add("active", "animate");
    }

    const drawerInner = cartDrawer.querySelector(".drawer__inner");
    if (drawerInner) drawerInner.scrollTop = scrollTop;

    const hasItems = cartDrawer.querySelector(".cart-item") !== null;
    cartDrawer.classList.toggle("is-empty", !hasItems);

    const drawerItems = cartDrawer.querySelector("cart-drawer-items");
    if (drawerItems) drawerItems.classList.toggle("is-empty", !hasItems);

    const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", () => cartDrawer.close());
  }

  if (sections["cart-icon-bubble"]) {
    const cartBubble = document.getElementById("cart-icon-bubble");
    if (cartBubble) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sections["cart-icon-bubble"];
      const newContent = tempDiv.querySelector(".shopify-section");
      if (newContent) {
        cartBubble.innerHTML = newContent.innerHTML;
      } else {
        cartBubble.innerHTML = tempDiv.innerHTML;
      }
    }
  }
}

// Free Gift Manager Class - Uses static config, no product fetching
class FreeGiftManager {
  constructor() {
    this.isProcessing = false;
    this.lastOperationTime = 0; // Track when we last added/removed gift
    this.lastCheckTime = 0; // Debounce checks
    this.CHECK_DEBOUNCE = 100; // ms between checks (reduced for faster response)
  }

  /**
   * Main entry point, called by the cart_changed event.
   * Trusts the cart object it receives and acts immediately.
   */
  async checkAndManage(cart) {
    if (this.isProcessing || !cart) {
      return;
    }

    // Debounce rapid checks
    const now = Date.now();
    if (now - this.lastCheckTime < this.CHECK_DEBOUNCE) {
      return;
    }
    this.lastCheckTime = now;

    this.isProcessing = true;
    Logger.log(`[FreeGiftManager] Checking cart state...`);

    try {
      const currency = cart.currency || "AUD";
      if (!CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency)) {
        this.isProcessing = false;
        return;
      }

      const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
      if (!tiers) {
        this.isProcessing = false;
        return;
      }

      const freeGiftItems = this.findAllFreeGiftsInCart(cart);
      const hasGift = freeGiftItems.length > 0;
      const cartTotal = this.calculateCartTotalExcludingGift(cart);
      const regularItemCount = this.countRegularItems(cart);

      // Determine which tier has the gift (default to TIER_2 for backward compatibility)
      const giftTier = CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2";
      const giftThreshold = tiers[giftTier];
      // GWP PAUSED: when disabled, shouldHaveGift is forced false so the
      // existing remove-branch strips any gift already in the cart and the
      // add-branch never fires.
      const shouldHaveGift =
        CART_ENHANCEMENT_CONFIG.GWP_ENABLED === false
          ? false
          : cartTotal >= giftThreshold && regularItemCount > 0;

      Logger.log(
        `[FreeGiftManager] State: shouldHaveGift=${shouldHaveGift}, hasGift=${hasGift}`,
        {
          giftTier,
          giftThreshold: giftThreshold / 100,
          cartTotal: cartTotal / 100,
          regularItemCount,
          giftCount: freeGiftItems.length,
        },
      );

      // PREVENT DUPLICATE GIFTS: Remove extras if more than 1
      if (freeGiftItems.length > 1) {
        Logger.log(
          `[FreeGiftManager] Found ${freeGiftItems.length} gifts - removing duplicates`,
        );
        // Keep first, remove the rest
        for (let i = 1; i < freeGiftItems.length; i++) {
          await this._removeFreeGift(freeGiftItems[i]);
        }
        return; // Exit after cleanup, next cycle will handle correctly
      }

      // PREVENT GIFT-ONLY CART: Remove gift if no regular items
      if (regularItemCount === 0 && hasGift) {
        Logger.log(`[FreeGiftManager] Cart has only gift(s), removing...`);
        await this._removeFreeGift(freeGiftItems[0]);
        return;
      }

      if (shouldHaveGift && !hasGift) {
        Logger.log(`[FreeGiftManager] Action: Adding gift.`);
        await this._addFreeGift();
      } else if (!shouldHaveGift && hasGift) {
        Logger.log(`[FreeGiftManager] Action: Removing gift.`);
        await this._removeFreeGift(freeGiftItems[0]);
      }
    } catch (error) {
      Logger.error("[FreeGiftManager] Error:", error);
    } finally {
      this.isProcessing = false;
      Logger.log(`[FreeGiftManager] Check complete.`);
    }
  }

  /**
   * Placeholder for checkout button updates (disabled feature)
   */
  updateCheckoutButton(hasRegularItems) {
    // Checkout button is never disabled - users can always proceed
  }

  /**
   * Helper to calculate cart total excluding free gift
   * Checks _free_gift property only (not handle) so gift product can be changed
   */
  calculateCartTotalExcludingGift(cart) {
    if (!cart || !cart.items) return 0;

    let total = cart.total_price;
    for (const item of cart.items) {
      if (item.properties && item.properties._free_gift === "true") {
        total -= item.price * item.quantity;
      }
    }
    return Math.max(0, total);
  }

  /**
   * Helper to check if free gift is in cart (returns first match)
   * Checks _free_gift property only
   */
  findFreeGiftInCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(
      (item) => item.properties && item.properties._free_gift === "true",
    );
  }

  /**
   * Helper to find ALL free gifts in cart (for duplicate detection)
   * Checks _free_gift property only
   */
  findAllFreeGiftsInCart(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(
      (item) => item.properties && item.properties._free_gift === "true",
    );
  }

  /**
   * Helper to count regular (non-gift) items
   */
  countRegularItems(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.filter(
      (item) => !(item.properties && item.properties._free_gift === "true"),
    ).length;
  }

  /**
   * Adds the free gift to the cart and immediately refreshes UI.
   * Uses sections API to minimize network requests.
   */
  async _addFreeGift() {
    this.lastOperationTime = Date.now();
    const fetchFn = window._originalFetch || window.fetch;
    const variantId = CART_ENHANCEMENT_CONFIG.FREE_GIFT_VARIANT_ID;

    Logger.log("[FreeGiftManager] Action: Adding gift...");

    try {
      // Add gift AND get sections in one request
      const response = await fetchFn(_CART_ADD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _free_gift: "true" },
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: _CART_URL,
        }),
      });

      this.lastOperationTime = Date.now();

      if (response.ok) {
        const data = await response.json();

        // Update UI from sections if available
        if (data.sections) {
          updateCartDrawerFromSections(data.sections);
        } else {
          // Fallback to full refresh
          await refreshCartDrawerWithReinit();
        }

        // Progress bar doesn't need updating here - it excludes free gifts from
        // its total calculation, so the value doesn't change when a gift is added.
        // CartWatcher will update the counter on the next cycle.
        Logger.log("[FreeGiftManager] Gift added and UI refreshed");
      }
    } catch (e) {
      Logger.error("[FreeGiftManager] Add gift failed:", e);
    }
  }

  /**
   * Removes the free gift from the cart and immediately refreshes UI.
   * Uses sections API - /cart/update.js returns full cart + sections in ONE request.
   */
  async _removeFreeGift(giftItem) {
    if (!giftItem || !giftItem.key) return;

    this.lastOperationTime = Date.now();
    const fetchFn = window._originalFetch || window.fetch;

    Logger.log("[FreeGiftManager] Action: Removing gift...");

    try {
      // Remove gift AND get sections in one request
      const response = await fetchFn(`${routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          updates: { [giftItem.key]: 0 },
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: _CART_URL,
        }),
      });

      this.lastOperationTime = Date.now();

      if (response.ok) {
        const cart = await response.json();

        // Update progress bar and counter with cart data from response
        if (window.cartEnhancements) {
          window.cartEnhancements.progressBar.update(cart);
          window.cartEnhancements.itemCounter.update(cart);
        }

        // Update UI from sections if available
        if (cart.sections) {
          updateCartDrawerFromSections(cart.sections);
        } else {
          // Fallback to full refresh if sections not in response
          await refreshCartDrawerWithReinit();
        }

        Logger.log("[FreeGiftManager] Gift removed and UI refreshed");
      }
    } catch (e) {
      Logger.error("[FreeGiftManager] Remove gift failed:", e);
    }
  }

}

// Free Gift Display Manager
// Free Gift Display - No longer needed (gift displayed in line items)
class FreeGiftDisplay {
  update(cart) {
    // Free gift is now displayed in the line items, not in a separate section
  }
}

// Mystery Gift Manager Class
// Handles mystery gift eligibility, selection, and cart management
class MysteryGiftManager {
  constructor() {
    this.mysteryGiftProduct = null;
    this.selectedVariant = null;
    this.isEligible = false;
    this.isAddingToCart = false;
    this.isRemovingFromCart = false;

    // Initialize on construction
    this.init();
  }

  init() {
    // Check for email campaign on load
    this.checkForEmailCampaign();

    // Fetch the mystery gift product
    this.fetchMysteryGiftProduct();
  }

  /**
   * Check URL parameters for email campaign triggers
   * Sets a cookie if found
   */
  checkForEmailCampaign() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams) {
        const searchString = urlParams.toString().toLowerCase();
        const triggers = CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_URL_TRIGGERS;

        for (const trigger of triggers) {
          if (searchString.includes(trigger.toLowerCase())) {
            // Set cookie for configured duration
            const expiryDate = new Date();
            expiryDate.setTime(
              expiryDate.getTime() +
                CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_DURATION_HOURS *
                  60 *
                  60 *
                  1000,
            );
            document.cookie = `${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/`;
            Logger.log("[MysteryGift] Email campaign detected, cookie set");
            return true;
          }
        }
      }
    } catch (error) {
      Logger.error("[MysteryGift] Error checking email campaign:", error);
    }
    return false;
  }

  /**
   * Check if mystery gift cookie exists
   */
  hasCookie() {
    return document.cookie
      .split(";")
      .some((item) =>
        item
          .trim()
          .startsWith(`${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=`),
      );
  }

  /**
   * Check if mystery gift is already in cart
   */
  hasGiftInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(
      (item) => item.properties && item.properties._mystery_gift === "true",
    );
  }

  /**
   * Get mystery gift item from cart
   */
  getGiftFromCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(
      (item) => item.properties && item.properties._mystery_gift === "true",
    );
  }

  /**
   * Set up mystery gift product data from the hardcoded variant ID.
   * No network request needed — avoids up to 6 fetch calls on page load.
   */
  async fetchMysteryGiftProduct() {
    const variantId = CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_VARIANT_ID;
    if (!variantId) {
      Logger.log("[MysteryGift] No variant ID configured");
      return;
    }

    this.mysteryGiftProduct = {
      title: "Mystery Gift",
      handle: "mystery-gift",
      images: [],
      variants: [
        {
          id: Number(variantId),
          title: "Default",
          available: true,
        },
      ],
    };
    Logger.log("[MysteryGift] Using hardcoded variant ID:", variantId);
  }

  /**
   * Check eligibility for mystery gift
   * Returns true if user is eligible and gift is not already in cart
   */
  checkEligibility(cart) {
    // Must have cookie from email campaign
    const hasCookie = this.hasCookie();

    // Must have items in cart
    const hasItems = cart && cart.item_count > 0;

    // Must not already have mystery gift in cart
    const alreadyInCart = this.hasGiftInCart(cart);

    // Must have product data loaded
    const hasProduct = this.mysteryGiftProduct !== null;

    this.isEligible = hasCookie && hasItems && !alreadyInCart && hasProduct;

    Logger.log("[MysteryGift] Eligibility check:", {
      hasCookie,
      hasItems,
      alreadyInCart,
      hasProduct,
      isEligible: this.isEligible,
    });

    return this.isEligible;
  }

  /**
   * Get available variants for selection
   */
  getAvailableVariants() {
    if (!this.mysteryGiftProduct || !this.mysteryGiftProduct.variants) {
      return [];
    }
    return this.mysteryGiftProduct.variants.filter(
      (v) => v.available !== false,
    );
  }

  /**
   * Add mystery gift to cart with selected variant
   */
  async addToCart(variantId) {
    if (this.isAddingToCart || this.isRemovingFromCart) {
      Logger.log("[MysteryGift] Operation in progress, skipping");
      return false;
    }

    if (!variantId) {
      Logger.error("[MysteryGift] No variant ID provided");
      return false;
    }

    try {
      this.isAddingToCart = true;
      const fetchFn = window._originalFetch || window.fetch;

      // Find the variant
      const variant = this.mysteryGiftProduct?.variants?.find(
        (v) => v.id.toString() === variantId.toString(),
      );
      if (variant) {
        this.selectedVariant = variant;
      }

      const response = await fetchFn(_CART_ADD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId.toString(),
          quantity: 1,
          properties: {
            _mystery_gift: "true",
          },
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: _CART_URL,
        }),
      });

      if (response.ok) {
        Logger.log("[MysteryGift] Added to cart");

        // Hide selection UI
        this.hideSelectionUI();

        // Apply sections from the same response — no follow-up cart fetch needed.
        const data = await response.json();
        if (data.sections) {
          updateCartDrawerFromSections(data.sections);
          // Re-bind counter element references after innerHTML replacement.
          // Mystery gift is free, so progress bar state doesn't change here;
          // CartWatcher will pick up any other changes on its next cycle.
          if (window.cartDrawerEnhancements) {
            window.cartDrawerEnhancements.itemCounter.reinitElements();
          }
        }

        return true;
      } else {
        Logger.error("[MysteryGift] Failed to add to cart:", response.status);
        return false;
      }
    } catch (error) {
      Logger.error("[MysteryGift] Error adding to cart:", error);
      return false;
    } finally {
      this.isAddingToCart = false;
    }
  }

  /**
   * Remove mystery gift from cart (when cart becomes empty or ineligible)
   */
  async removeFromCart(cart) {
    if (this.isRemovingFromCart || this.isAddingToCart) {
      return false;
    }

    const giftItem = this.getGiftFromCart(cart);
    if (!giftItem) {
      return false;
    }

    try {
      this.isRemovingFromCart = true;
      const fetchFn = window._originalFetch || window.fetch;

      const response = await fetchFn("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: _CART_URL,
        }),
      });

      if (response.ok) {
        Logger.log("[MysteryGift] Removed from cart");
        this.selectedVariant = null;

        // /cart/change.js returns the full cart object plus a `sections` key.
        const updatedCart = await response.json();

        if (updatedCart.sections) {
          updateCartDrawerFromSections(updatedCart.sections);
        }

        if (window.cartDrawerEnhancements) {
          window.cartDrawerEnhancements.itemCounter.reinitElements();
          window.cartDrawerEnhancements.progressBar.update(updatedCart, true);
          window.cartDrawerEnhancements.itemCounter.update(updatedCart);
        }

        return true;
      }

      return false;
    } catch (error) {
      Logger.error("[MysteryGift] Error removing from cart:", error);
      return false;
    } finally {
      this.isRemovingFromCart = false;
    }
  }

  /**
   * Check and manage mystery gift based on cart state
   */
  async checkAndManage(cart) {
    // Skip if operations in progress
    if (this.isAddingToCart || this.isRemovingFromCart) {
      return;
    }

    // GWP PAUSED: never offer the mystery gift. Strip any that's already in
    // the cart and keep the selection UI hidden.
    if (CART_ENHANCEMENT_CONFIG.GWP_ENABLED === false) {
      this.hideSelectionUI();
      if (cart && this.hasGiftInCart(cart)) {
        await this.removeFromCart(cart);
      }
      return;
    }

    // Remove if cart is empty or has only the mystery gift
    if (!cart || cart.item_count === 0) {
      return;
    }

    // Count non-mystery-gift items
    const regularItemCount = cart.items.filter((item) => {
      return !(item.properties && item.properties._mystery_gift === "true");
    }).length;

    // If only mystery gift in cart (no regular items), remove it
    if (regularItemCount === 0 && this.hasGiftInCart(cart)) {
      await this.removeFromCart(cart);
      return;
    }

    // Check eligibility and show/hide selection UI
    const isEligible = this.checkEligibility(cart);

    if (isEligible) {
      this.showSelectionUI();
    } else {
      this.hideSelectionUI();
    }
  }

  /**
   * Show the mystery gift selection UI
   */
  showSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (container) {
      container.style.display = "block";
      this.renderSelectionUI();
    }
  }

  /**
   * Hide the mystery gift selection UI
   */
  hideSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (container) {
      container.style.display = "none";
    }
  }

  /**
   * Render the mystery gift selection UI content
   */
  renderSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (!container || !this.mysteryGiftProduct) return;

    const variants = this.getAvailableVariants();
    if (variants.length === 0) return;

    // Get product image
    const productImage =
      this.mysteryGiftProduct.images?.[0]?.src ||
      this.mysteryGiftProduct.image?.src ||
      "";

    container.innerHTML = `
      <div class="mystery-gift-selection__inner">
        <div class="mystery-gift-selection__image">
          ${productImage ? `<img src="${productImage}" alt="${this.mysteryGiftProduct.title}" />` : ""}
        </div>
        <div class="mystery-gift-selection__content">
          <h4 class="mystery-gift-selection__title">CLAIM YOUR FREE MYSTERY GIFT!</h4>
          <p class="mystery-gift-selection__subtitle">Select your size:</p>
          <select id="mystery-gift-variant-select" class="mystery-gift-selection__select">
            <option value="">Choose size...</option>
            ${variants.map((v) => `<option value="${v.id}">${v.title}</option>`).join("")}
          </select>
          <button id="mystery-gift-add-btn" class="mystery-gift-selection__button" disabled>
            ADD TO CART
          </button>
        </div>
      </div>
    `;

    // Add event listeners
    const select = container.querySelector("#mystery-gift-variant-select");
    const button = container.querySelector("#mystery-gift-add-btn");

    if (select && button) {
      select.addEventListener("change", (e) => {
        button.disabled = !e.target.value;
      });

      button.addEventListener("click", async () => {
        const variantId = select.value;
        if (variantId) {
          button.disabled = true;
          button.textContent = "ADDING...";
          await this.addToCart(variantId);
        }
      });
    }
  }
}

// Item Counter Manager
class ItemCounter {
  constructor() {
    // State tracking
    this.state = {
      count: 0,
      isVisible: false,
    };

    this.reinitElements();
  }

  reinitElements() {
    this.counterElement = document.getElementById("cart-drawer-item-counter");
    this.countElement = document.getElementById("cart-item-count");
  }

  update(cart) {
    // Defensive: skip malformed cart shapes (e.g. the raw /cart/add.js
    // line-item response has no item_count). Writing "undefined" into
    // the counter would flicker the visible number.
    if (!cart || typeof cart.item_count !== "number") return;

    const countElement = document.getElementById("cart-item-count");
    if (countElement) {
      countElement.textContent = cart.item_count;
    }

    this.state.count = cart.item_count;
    this.state.isVisible = cart.item_count > 0;
  }
}

// Helper to refresh cart drawer with proper custom element reinitialization
async function refreshCartDrawerWithReinit(
  expectedItemCount = null,
  retryCount = 0,
) {
  const fetchFn = window._originalFetch || window.fetch;
  const maxRetries = 1;
  const cartDrawer = document.querySelector("cart-drawer");

  if (!cartDrawer) return;

  try {
    const response = await fetchFn(
      CART_DRAWER_SECTIONS_URL,
    );
    if (!response.ok) {
      Logger.error("[Refresh] Failed to fetch sections");
      return;
    }

    const data = await response.json();
    const cartDrawerHtml = data["cart-drawer"];
    const cartBubbleHtml = data["cart-icon-bubble"];

    if (cartDrawerHtml) {
      const wasActive = cartDrawer.classList.contains("active");
      const scrollTop =
        cartDrawer.querySelector(".drawer__inner")?.scrollTop || 0;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cartDrawerHtml;
      const newCartItems = tempDiv.querySelectorAll(".cart-item");

      if (
        expectedItemCount !== null &&
        newCartItems.length !== expectedItemCount &&
        retryCount < maxRetries
      ) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return refreshCartDrawerWithReinit(expectedItemCount, retryCount + 1);
      }

      const newSection =
        tempDiv.querySelector("cart-drawer") || tempDiv.firstElementChild;

      if (newSection) {
        cartDrawer.innerHTML = newSection.innerHTML;

        if (wasActive) {
          cartDrawer.classList.add("active", "animate");
        }

        const drawerInner = cartDrawer.querySelector(".drawer__inner");
        if (drawerInner) {
          drawerInner.scrollTop = scrollTop;
        }

        const hasItems = cartDrawer.querySelector(".cart-item") !== null;
        cartDrawer.classList.toggle("is-empty", !hasItems);

        const drawerItems = cartDrawer.querySelector("cart-drawer-items");
        if (drawerItems) {
          drawerItems.classList.toggle("is-empty", !hasItems);
        }

        const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
        if (overlay) {
          overlay.addEventListener("click", () => cartDrawer.close());
        }
      }
    }

    if (cartBubbleHtml) {
      const cartBubble = document.getElementById("cart-icon-bubble");
      if (cartBubble) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = cartBubbleHtml;
        const newContent =
          tempDiv.querySelector(".shopify-section") ||
          tempDiv.firstElementChild;
        if (newContent) {
          cartBubble.innerHTML = newContent.innerHTML;
        }
      }
    }
  } catch (error) {
    Logger.error("[Refresh] Error:", error);
  }
}

/**
 * Surgical version of refreshCartDrawerWithReinit — instead of wiping the
 * entire <cart-drawer> innerHTML and re-attaching event listeners, swap
 * only the parts that actually change after an add:
 *   - .drawer__cart-items-wrapper (the items list — the LIs the user
 *     rendered client-side via the fast-add path)
 *   - #cart-drawer-state (JSON tag with item_count / total_price)
 *   - .totals__subtotal-value (footer subtotal display)
 *   - cart-icon-bubble (count badge)
 *
 * Leaves timer, progress bar, announcement bar, header, mystery-gift
 * block, footer CTAs, and any custom-element children with attached
 * state untouched. Eliminates the flicker between client-rendered LIs
 * and server-rendered LIs that a full innerHTML swap would cause.
 *
 * Falls back to refreshCartDrawerWithReinit if the fetched HTML doesn't
 * have the expected structure (e.g. the cart became empty — Liquid
 * elides the items-wrapper and we need the full chrome reset).
 */
async function surgicalRefreshCartDrawer() {
  const fetchFn = window._originalFetch || window.fetch;
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;

  try {
    // Two parallel fetches:
    //   - /cart?sections=cart-drawer-items,cart-icon-bubble → items HTML
    //     (~30-50ms server render, drives the surgical wrapper swap below)
    //   - /cart.js → full cart object with real items[] (repairs gift-check
    //     staleness — after a fast-add cartEnhancements.lastCart still has
    //     the pre-add items[], so FreeGiftManager/MysteryGiftManager would
    //     otherwise run against stale data near threshold crossings)
    // Running both in parallel: total latency bounded by max(), not sum.
    const [sectionsResp, cartResp] = await Promise.all([
      fetchFn(CART_ITEMS_SECTIONS_URL),
      fetchFn(CART_JS_URL).catch(() => null),
    ]);
    if (!sectionsResp.ok) {
      Logger.error("[SurgicalRefresh] Failed to fetch sections");
      return;
    }

    const data = await sectionsResp.json();
    // Response shape now keyed by cart-drawer-items (was cart-drawer)
    // since the section split. The HTML is just the .drawer__cart-items-wrapper
    // — no surrounding cart-drawer chrome to discard.
    const cartItemsHtml = data["cart-drawer-items"];
    const cartBubbleHtml = data["cart-icon-bubble"];

    if (cartItemsHtml) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cartItemsHtml;

      const newWrapper = tempDiv.querySelector(".drawer__cart-items-wrapper");
      const newCartItems = tempDiv.querySelectorAll(".cart-item");

      // Empty-cart case (Liquid elides the wrapper) — fall through to
      // full-swap so the empty-state block + is-empty layout are restored.
      if (!newWrapper) {
        if (typeof refreshCartDrawerWithReinit === "function") {
          return refreshCartDrawerWithReinit();
        }
        return;
      }

      const currentWrapper = cartDrawer.querySelector(".drawer__cart-items-wrapper");
      if (currentWrapper && newWrapper) {
        // Preserve scroll position — the wrapper is the scrollable list
        // on mobile; replacing it would jump the user to top.
        const scrollTop = currentWrapper.scrollTop || 0;
        currentWrapper.replaceWith(newWrapper);
        try { newWrapper.scrollTop = scrollTop; } catch (_) {}
      } else if (!currentWrapper) {
        // No wrapper currently in DOM — probably because beginAdd's
        // synthetic ul never ran (rare). Append into #CartDrawer-CartItems.
        const container = cartDrawer.querySelector("#CartDrawer-CartItems");
        if (container) container.appendChild(newWrapper);
      }

      // #cart-drawer-state JSON and .totals__subtotal-value live in the
      // parent cart-drawer section (chrome), not in cart-drawer-items.
      // They were already updated client-side by _applyFastAddRender;
      // the /cart.js block below will sync them from authoritative data.

      // Footer disabled state on the checkout button — Liquid adds
      // `disabled` when cart == empty. After an add we never see empty
      // here (newWrapper exists), so just ensure it's enabled.
      const checkoutBtn = cartDrawer.querySelector("#CartDrawer-Checkout");
      if (checkoutBtn) checkoutBtn.removeAttribute("disabled");

      // Drop is-empty markers — items present.
      cartDrawer.classList.remove("is-empty");
      const drawerItemsEl = cartDrawer.querySelector("cart-drawer-items");
      if (drawerItemsEl) drawerItemsEl.classList.remove("is-empty");

      Logger.log("[SurgicalRefresh] Swapped items wrapper + totals", {
        itemCount: newCartItems.length,
      });
    }

    if (cartBubbleHtml) {
      const cartBubble = document.getElementById("cart-icon-bubble");
      if (cartBubble) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = cartBubbleHtml;
        const newContent =
          tempDiv.querySelector(".shopify-section") || tempDiv.firstElementChild;
        if (newContent) cartBubble.innerHTML = newContent.innerHTML;
      }
    }

    // Repair gift-check staleness using the parallel /cart.js result.
    // Fast-add updated lastCart's item_count + total_price but left
    // items[] stale (no way to derive a full items list from the add
    // response without the cart-drawer section). Now that we have the
    // real cart, replace lastCart + storage + re-run gift managers.
    if (cartResp && cartResp.ok) {
      try {
        const realCart = await cartResp.json();
        const ce = window.cartEnhancements;
        if (ce && realCart && typeof realCart.item_count === "number") {
          ce.lastCart = realCart;
          ce._lastCartFetchTime = Date.now();
          if (ce.cartWatcher && typeof ce.cartWatcher.storeCart === "function") {
            // Restore CartWatcher's stored cart so external mutations
            // (Visually.io, manual cart edits) diff against real items[].
            ce.cartWatcher.storeCart(realCart);
          }

          // Sync chrome that doesn't live in cart-drawer-items section:
          // #cart-drawer-state JSON tag + footer .totals__subtotal-value.
          // These are in the parent cart-drawer section, which we
          // intentionally don't re-fetch (the whole point of the split).
          const stateEl = cartDrawer.querySelector("#cart-drawer-state");
          if (stateEl) {
            try {
              const cur = JSON.parse(stateEl.textContent);
              stateEl.textContent = JSON.stringify({
                item_count: realCart.item_count,
                total_price: realCart.total_price,
                total_discount: typeof realCart.total_discount === "number" ? realCart.total_discount : 0,
                currency: realCart.currency || cur.currency,
              });
            } catch (_) {}
          }
          const subtotalEl = cartDrawer.querySelector(".totals__subtotal-value");
          if (subtotalEl) {
            const SH = window.Shopify;
            const moneyFmt =
              (SH && SH.money_format) ||
              (window.theme && window.theme.moneyFormat) ||
              null;
            let display;
            if (SH && typeof SH.formatMoney === "function" && moneyFmt) {
              try { display = SH.formatMoney(realCart.total_price, moneyFmt); } catch (_) {}
            }
            if (!display) display = "$" + (realCart.total_price / 100).toFixed(2);
            subtotalEl.textContent = " " + display;
          }

          // onCartUpdate has the 1500ms lastOperationTime guard inside,
          // so this is safe even if a free-gift add just landed.
          if (typeof ce.onCartUpdate === "function") {
            await ce.onCartUpdate(realCart);
          }
        }
      } catch (e) {
        Logger.warn("[SurgicalRefresh] /cart.js apply failed:", e);
      }
    }
  } catch (error) {
    Logger.error("[SurgicalRefresh] Error:", error);
  }
}

// Helper to refresh cart drawer content by triggering Shopify's native section rendering
async function refreshCartDrawerContent() {
  try {
    const fetchFn = window._originalFetch || window.fetch;

    // Fetch fresh sections from Shopify
    const response = await fetchFn(
      CART_DRAWER_SECTIONS_URL,
    );
    if (!response.ok) {
      Logger.error("Failed to fetch cart sections");
      return false;
    }

    const data = await response.json();

    // Try to use CartDrawerItems native rendering if available
    const cartDrawerItems = document.querySelector("cart-drawer-items");
    if (
      cartDrawerItems &&
      typeof cartDrawerItems.getSectionsToRender === "function"
    ) {
      // Use native section rendering
      const sections = cartDrawerItems.getSectionsToRender();
      sections.forEach((section) => {
        const sectionHtml = data.sections[section.section];
        if (sectionHtml) {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, "text/html");
            const newElement =
              doc.querySelector(section.selector) ||
              doc.querySelector(".shopify-section");
            if (newElement) {
              elementToReplace.innerHTML = newElement.innerHTML;
            }
          }
        }
      });
      Logger.log("Cart drawer content refreshed via native method");
    } else {
      // Fallback: Replace cart drawer content directly
      if (data.sections && data.sections["cart-drawer"]) {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartDrawerSection = document.getElementById("CartDrawer");

        if (cartDrawerSection) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            data.sections["cart-drawer"],
            "text/html",
          );
          const newContent =
            doc.querySelector("#CartDrawer") ||
            doc.querySelector(".drawer__inner");

          if (newContent) {
            // Store scroll position and drawer state
            const drawerInner = cartDrawer?.querySelector(".drawer__inner");
            const scrollTop = drawerInner?.scrollTop || 0;
            const isActive = cartDrawer?.classList.contains("active");

            // Update content
            cartDrawerSection.innerHTML = newContent.innerHTML;

            // Restore state
            if (drawerInner) drawerInner.scrollTop = scrollTop;
            if (isActive && cartDrawer) {
              cartDrawer.classList.add("active", "animate");
            }

            Logger.log("Cart drawer content refreshed via fallback");
          }
        }
      }
    }

    // Update cart icon bubble
    if (data.sections && data.sections["cart-icon-bubble"]) {
      const cartBubbleSection = document.getElementById("cart-icon-bubble");
      if (cartBubbleSection) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          data.sections["cart-icon-bubble"],
          "text/html",
        );
        const newBubble = doc.querySelector(".shopify-section");
        if (newBubble) {
          cartBubbleSection.innerHTML = newBubble.innerHTML;
        }
      }
    }

    // Update empty states
    const cartDrawer = document.querySelector("cart-drawer");
    const drawerItems = cartDrawer?.querySelector("cart-drawer-items");
    const isEmpty = !drawerItems?.querySelector(".cart-item");

    if (cartDrawer) cartDrawer.classList.toggle("is-empty", isEmpty);
    if (drawerItems) drawerItems.classList.toggle("is-empty", isEmpty);

    return true;
  } catch (error) {
    Logger.error("Error refreshing cart drawer:", error);
    return false;
  }
}

// Main Cart Drawer Enhancements Manager
class CartDrawerEnhancements {
  constructor() {
    this.timer = new CartTimer();
    this.progressBar = new CartProgressBar();
    this.freeGiftManager = new FreeGiftManager();
    this.freeGiftDisplay = new FreeGiftDisplay();
    this.mysteryGiftManager = new MysteryGiftManager();
    this.itemCounter = new ItemCounter();
    this.cartWatcher = new CartWatcher();
    this.cartDrawer = null;
    this.lastCart = null;
    this.lastCartWatcherEventTime = 0; // Track when CartWatcher last emitted an event
    this.pendingUpdate = null; // Debounce pending updates
    this.UPDATE_DEBOUNCE = 200; // ms to debounce updates (batch rapid events from bundle apps)
    this.init();
  }

  init() {
    // Expose globally so cart.js can update state before section replacement
    window.cartEnhancements = this;

    // Store original fetch early (before any other scripts might intercept it)
    // This is used by CartWatcher and FreeGiftManager to avoid circular dependencies
    if (!window._originalFetch) {
      window._originalFetch = window.fetch.bind(window);
    }

    // Fast-add fetch interceptor: strip `cart-drawer` from sections= on
    // /cart/add.js POST so the server stops rendering the heavy cart-drawer
    // Liquid section (the dominant ~500-1500ms latency on add). We render
    // the new line items client-side from response.items[] in our
    // renderContents override, then fire a background section refresh to
    // sync drawer chrome (progress bar / footer total) with server truth.
    //
    // cart-icon-bubble is small and cheap to render server-side — leave
    // it in sections= so the bubble update is bundled.
    //
    // Patch the CartDrawer prototype here (synchronous, same tick as the
    // interceptor) — not in setup() — so a click that fires before
    // DOMContentLoaded still hits the patched renderContents instead of
    // the original (which would crash on the stripped section).
    this._patchCartDrawerRenderContents();
    this._installFastAddInterceptor();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Install a one-time wrapper around window.fetch that strips
   * `cart-drawer` from the sections= form field on POST /cart/add.js.
   * The wrapper preserves everything else and is a no-op for non-add
   * requests, so other apps (Visually.io, etc.) are unaffected.
   *
   * Idempotent — sets a flag on window so re-init doesn't double-wrap.
   */
  /**
   * Monkey-patch CartDrawer.prototype.renderContents to handle the
   * fast-path response (items[] present, cart-drawer section missing
   * because our fetch interceptor stripped it). Patches the prototype —
   * not a single instance — so it survives section innerHTML swaps that
   * detach the old cart-drawer node from the DOM and use a fresh one.
   *
   * If the CartDrawer class hasn't been registered yet (script ordering),
   * polls customElements.whenDefined('cart-drawer') and patches once it
   * resolves. Idempotent.
   */
  _patchCartDrawerRenderContents() {
    const self = this;
    const apply = (ctor) => {
      if (!ctor || !ctor.prototype) return;
      if (ctor.prototype._fastAddPatched) return;
      const original = ctor.prototype.renderContents;
      if (typeof original !== "function") return;
      // Flag the manager — interceptor reads this and only strips
      // cart-drawer from sections= once the patched renderContents is in
      // place. Avoids a window where a stripped response could hit the
      // unpatched original and crash on the missing section.
      self._fastAddReady = true;
      ctor.prototype.renderContents = function (parsedState) {
        // /cart/add.js responses come in two shapes:
        // - Form POST (product-form, sticky-add-to-cart): line item AT ROOT
        //   { id, key, variant_id, title, ... , sections: {} }
        // - JSON POST with items[] (bundle-picker): { items: [...], sections: {} }
        // Normalise to an items[] for the fast-path render.
        const responseItems = (() => {
          if (!parsedState) return [];
          if (Array.isArray(parsedState.items)) return parsedState.items;
          // Treat root-level line item as a single-item array.
          if (parsedState.key && parsedState.variant_id) return [parsedState];
          return [];
        })();
        // Fast-path is engaged whenever our interceptor flagged it AND
        // the response carries no full cart-drawer section. With the
        // items-section refactor, the response will instead carry
        // cart-drawer-items HTML — that's the preferred path inside
        // _applyFastAddRender. Without it we fall back to the client
        // renderer, which still works.
        const isFastPath =
          self._fastAddPending &&
          responseItems.length > 0 &&
          (!parsedState.sections || !parsedState.sections["cart-drawer"]);
        if (isFastPath) {
          // Original wipes drawer__inner.is-empty first — mirror so the
          // grid → flex layout switch happens before we touch DOM.
          const inner = this.querySelector(".drawer__inner");
          if (inner && inner.classList.contains("is-empty")) {
            inner.classList.remove("is-empty");
          }
          this.productId = parsedState.id;
          const applied = self._applyFastAddRender(parsedState, responseItems);
          if (applied) {
            // Fire the same public events listeners expect (analytics, etc.).
            const drawer = this;
            setTimeout(() => {
              const overlay = drawer.querySelector("#CartDrawer-Overlay");
              if (overlay) overlay.addEventListener("click", drawer.close.bind(drawer));
              if (typeof drawer.open === "function") drawer.open();
              drawer.dispatchEvent(new CustomEvent("cart:open"));
              window.dispatchEvent(
                new CustomEvent("cart:updated", { detail: { cart: self.lastCart } }),
              );
            });
            return;
          }
          // Fast render bailed (edge-case item or no list to update) —
          // can't call original because the cart-drawer section is gone
          // from the response. Fall back to a background refresh.
          self._fastAddPending = false;
          if (typeof refreshCartDrawerWithReinit === "function") {
            refreshCartDrawerWithReinit();
          }
          return;
        }
        // Defensive: even if isFastPath was false for some unexpected
        // reason (response shape we don't recognise, _fastAddPending got
        // cleared early, etc.), the response may still be missing
        // sections['cart-drawer'] because our interceptor stripped it.
        // Calling original here would crash on null.innerHTML. Detect
        // the missing section and route to a background refresh instead.
        if (
          parsedState &&
          parsedState.sections &&
          !parsedState.sections["cart-drawer"]
        ) {
          self._fastAddPending = false;
          if (typeof refreshCartDrawerWithReinit === "function") {
            refreshCartDrawerWithReinit();
          }
          return;
        }
        // Normal bundled response — original behavior.
        return original.call(this, parsedState);
      };
      ctor.prototype._fastAddPatched = true;
    };

    // Already defined → patch now.
    const existing =
      window.customElements && typeof window.customElements.get === "function"
        ? window.customElements.get("cart-drawer")
        : null;
    if (existing) {
      apply(existing);
      return;
    }

    // Not yet defined (script-order race) — wait for it. whenDefined is
    // promise-based and resolves once customElements.define('cart-drawer')
    // fires. Until then, leave fast-add disabled.
    if (
      window.customElements &&
      typeof window.customElements.whenDefined === "function"
    ) {
      window.customElements
        .whenDefined("cart-drawer")
        .then(() => apply(window.customElements.get("cart-drawer")))
        .catch(() => {});
    }
  }

  _installFastAddInterceptor() {
    if (window._fastAddInterceptorInstalled) return;
    window._fastAddInterceptorInstalled = true;

    const originalFetch = window._originalFetch || window.fetch.bind(window);
    const self = this;

    window.fetch = function (input, init) {
      try {
        const url = typeof input === "string" ? input : input && input.url;
        const isPost = init && typeof init.method === "string" && init.method.toUpperCase() === "POST";
        // Only target the cart add endpoint — leave /cart/update, /cart/change,
        // /cart.js untouched. Also require the renderContents patch to be
        // in place; stripping cart-drawer before that would route the
        // response to the unpatched original and crash on the missing
        // section.
        if (
          self._fastAddReady &&
          url &&
          isPost &&
          /\/cart\/add(\.js)?(\?|$)/.test(url) &&
          init.body &&
          typeof init.body === "object" &&
          typeof init.body.get === "function" &&
          typeof init.body.set === "function"
        ) {
          const sections = init.body.get("sections");
          if (typeof sections === "string") {
            const list = sections
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const drawerIdx = list.indexOf("cart-drawer");
            if (drawerIdx !== -1) {
              // Substitute the small items section for the heavy full
              // drawer section. Server renders ~30-50ms instead of
              // ~500-1500ms, and the response carries server-rendered
              // items HTML that the fast-add path swaps in directly —
              // no client cart-item template needed.
              list[drawerIdx] = "cart-drawer-items";
              // Dedupe in case the caller already requested both.
              const deduped = Array.from(new Set(list));
              init.body.set("sections", deduped.join(","));
              // Mark this add as fast-path. renderContents reads this on
              // the response to decide which render flow to use.
              self._fastAddPending = true;
            }
          }
        }
      } catch (e) {
        Logger.warn("[FastAdd] interceptor error:", e);
      }
      return originalFetch.call(this, input, init);
    };
  }

  /**
   * Build a <li.cart-item> DOM node from a /cart/add.js line item,
   * mirroring the Liquid template in snippets/cart-drawer.liquid.
   * Returns null for edge-case items (free gift, mystery gift, bundle,
   * hidden, taxes) so the caller can fall back to bundled section
   * rendering for those — they have meaningfully different markup that
   * isn't worth replicating client-side.
   *
   * @param {object} item - response.items[N]
   * @param {number} indexHint - 1-based position for unique IDs
   * @returns {HTMLLIElement|null}
   */
  _renderCartItemLi(item, indexHint) {
    if (!item || !item.key) return null;

    const props = item.properties || {};
    const productType = item.product_type || "";
    // Edge cases — markup diverges substantially from the standard item
    // (free-gift badge instead of quantity widget, no link, etc).
    if (
      props._free_gift === "true" ||
      props._mystery_gift === "true" ||
      productType === "Hidden" ||
      productType === "Taxes" ||
      productType === "Bundle"
    ) {
      return null;
    }

    const esc = (s) => String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

    // Money formatter — prefer Shopify.formatMoney (theme exposes a
    // money_format string), fall back to "$X.YY".
    const fmt = (cents) => {
      if (cents == null || isNaN(cents)) return "";
      const SH = window.Shopify;
      const moneyFmt =
        (SH && SH.money_format) ||
        (window.theme && window.theme.moneyFormat) ||
        null;
      if (SH && typeof SH.formatMoney === "function" && moneyFmt) {
        try { return SH.formatMoney(cents, moneyFmt); } catch (_) {}
      }
      return "$" + (Number(cents) / 100).toFixed(2);
    };

    // Build sized image URLs. Shopify CDN supports a `width` param to serve a
    // downscaled image — match the Liquid `image_url: width: 240` + srcset so
    // the ~80px box renders crisply on 2x/3x displays.
    const sizedImgUrl = (raw, w) => {
      try {
        const u = new URL(raw, window.location.origin);
        u.searchParams.set("width", String(w));
        return u.toString();
      } catch (_) {
        return raw;
      }
    };
    const rawImg =
      (item.featured_image && item.featured_image.url) || item.image || "";
    const imgSrc = rawImg ? sizedImgUrl(rawImg, "240") : "";
    const imgSrcset = rawImg
      ? [80, 120, 160, 240]
          .map((w) => `${sizedImgUrl(rawImg, w)} ${w}w`)
          .join(", ")
      : "";
    const imgAlt = (item.featured_image && item.featured_image.alt) || item.product_title || "";
    const url = item.url || "#";

    // Pull Colour + non-colour variant options out of options_with_values.
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    const colourOpt = options.find((o) => o && (o.name === "Colour" || o.name === "Color"));
    const otherOpts = options.filter((o) => {
      if (!o || !o.name) return false;
      if (o.name === "Colour" || o.name === "Color") return false;
      const h = String(o.value || "").toLowerCase();
      return !h.includes("bundle");
    });
    const hasOnlyDefaultVariant = item.product_has_only_default_variant === true;

    // Price block — Liquid prefers compare_at_price strike-through when on
    // sale, then falls back to line-level discounts (original_price >
    // final_price), then regular price.
    const finalPrice = item.final_price;
    const originalPrice = item.original_price;
    const compareAt = item.variant_compare_at_price || item.compare_at_price || null;
    let priceHtml = "";
    if (compareAt && compareAt > finalPrice) {
      priceHtml =
        `<span class="cart-item__price-sale">${fmt(finalPrice)}</span>` +
        `<span class="cart-item__price-compare-at"><s>${fmt(compareAt)}</s></span>`;
    } else if (originalPrice > finalPrice) {
      priceHtml =
        `<span class="cart-item__price-sale">${fmt(finalPrice)}</span>` +
        `<span class="cart-item__price-compare-at"><s>${fmt(originalPrice)}</s></span>`;
    } else {
      priceHtml =
        `<span class="cart-item__price-regular">${fmt(originalPrice)}</span>`;
    }

    let optionsHtml = "";
    if (!hasOnlyDefaultVariant && colourOpt) {
      // Colour swatch: background-color falls through to the named color
      // (works for "Pink", "Red", etc.); the image overlay is best-effort.
      const swatchVal = esc(colourOpt.value);
      optionsHtml +=
        `<div class="cart-item__product-options">` +
          `<div class="cart-item__variant-option">` +
            `<div class="cart-item__option-swatch color-container" ` +
            `data-color="${swatchVal}" ` +
            `style="background-color:${swatchVal}"></div>` +
          `</div>` +
        `</div>`;
    }
    if (!hasOnlyDefaultVariant) {
      otherOpts.forEach((o) => {
        optionsHtml +=
          `<div class="cart-item__variant-title">${esc(o.name)}: ${esc(o.value)}</div>`;
      });
    }

    const imgHtml = imgSrc
      ? `<a href="${esc(url)}" tabindex="-1">` +
          `<img src="${esc(imgSrc)}" srcset="${esc(imgSrcset)}" sizes="80px" ` +
          `loading="lazy" class="cart-item__image" ` +
          `width="60" height="60" alt="${esc(imgAlt)}">` +
        `</a>`
      : "";

    const li = document.createElement("li");
    li.id = `CartDrawer-Item-${indexHint}`;
    li.className = "cart-item";
    li.setAttribute("data-item-key", item.key);
    li.setAttribute("role", "listitem");
    li.setAttribute("aria-label", "product");
    // Mark as optimistically rendered — the background section refresh
    // replaces these with the canonical server-rendered markup. Useful
    // for debugging and CSS-targeted polish if we ever need it.
    li.setAttribute("data-optimistic-item", "true");

    li.innerHTML =
      `<div class="cart-item__media">${imgHtml}</div>` +
      `<div class="cart-item__info">` +
        `<div class="cart-item__details">` +
          `<a href="${esc(url)}" class="cart-item__name" role="heading" aria-level="5">${esc(item.product_title || item.title || "")}</a>` +
          `<div class="cart-item__variant-options-row">` +
            optionsHtml +
            `<div class="cart-item__price-wrapper">${priceHtml}</div>` +
          `</div>` +
          `<div id="CartDrawer-LineItemError-${indexHint}" class="cart-item__error" role="alert">` +
            `<small class="cart-item__error-text"></small>` +
          `</div>` +
        `</div>` +
        `<div class="cart-item__quantity">` +
          `<div class="cart-item__quantity-widget">` +
            `<div class="loading-overlay hidden"></div>` +
            `<quantity-input class="quantity">` +
              `<button class="cart-item__quantity-widget-button no-js-hidden" name="minus" type="button" aria-label="Decrease quantity">−</button>` +
              `<span class="cart-item__quantity-widget-label">` +
                `<input class="quantity__input" type="number" name="updates[]" value="${item.quantity}" min="0" ` +
                `id="Drawer-quantity-${indexHint}" data-index="${indexHint}">` +
              `</span>` +
              `<button class="cart-item__quantity-widget-button no-js-hidden" name="plus" type="button" aria-label="Increase quantity">+</button>` +
            `</quantity-input>` +
          `</div>` +
        `</div>` +
        `<cart-remove-button id="CartDrawer-Remove-${indexHint}" data-index="${indexHint}" style="display:none;">` +
          `<button class="cart-item__remove-button button button--tertiary" aria-label="Remove">×</button>` +
        `</cart-remove-button>` +
      `</div>`;

    return li;
  }

  /**
   * Apply a fast-path render from a /cart/add.js response whose
   * `cart-drawer` section was stripped by the interceptor. Returns true
   * if the render succeeded and the caller should skip the normal
   * section swap; false to fall back to bundled rendering (e.g. one of
   * the items is a free gift and needs the Liquid badge markup).
   */
  _applyFastAddRender(parsedState, itemsOverride) {
    // Accept either shape: explicit items array from the caller (preferred,
    // since the patched renderContents already normalised both response
    // shapes), or fall back to parsedState.items.
    const items = Array.isArray(itemsOverride) && itemsOverride.length
      ? itemsOverride
      : (parsedState && Array.isArray(parsedState.items) ? parsedState.items : []);
    if (!items.length) return false;

    const drawer = this.cartDrawer || document.querySelector("cart-drawer");
    if (!drawer) return false;

    // Quantity-merge guard: when the user adds a variant already in the
    // cart, Shopify increments the existing line item's quantity (same
    // key returned with new quantity). Rendering a fresh LI for it would
    // produce a visible duplicate until the background refresh lands.
    // For this case skip the client-side render and let the background
    // refresh swap the canonical HTML in. Skeleton stays visible until
    // the refresh — still faster than waiting for a bundled add.
    const existingKeys = new Set(
      Array.from(
        drawer.querySelectorAll(".cart-item[data-item-key]:not([data-skeleton])"),
      ).map((el) => el.getAttribute("data-item-key")),
    );
    const hasMerge = items.some((it) => it && it.key && existingKeys.has(it.key));

    // Preferred path: the response includes server-rendered cart-drawer-items
    // HTML (because the fetch interceptor substituted cart-drawer →
    // cart-drawer-items). Swap the items-wrapper directly — no client
    // template to maintain, no risk of client vs server markup divergence,
    // and quantity-merge cases just work (the wrapper has the merged item
    // at its updated quantity).
    const itemsSectionHtml =
      parsedState.sections && parsedState.sections["cart-drawer-items"];
    if (itemsSectionHtml) {
      try {
        const tmp = document.createElement("div");
        tmp.innerHTML = itemsSectionHtml;
        const newWrapper = tmp.querySelector(".drawer__cart-items-wrapper");
        if (newWrapper) {
          const currentWrapper = drawer.querySelector(
            ".drawer__cart-items-wrapper",
          );
          if (currentWrapper) {
            const scrollTop = currentWrapper.scrollTop || 0;
            currentWrapper.replaceWith(newWrapper);
            try { newWrapper.scrollTop = scrollTop; } catch (_) {}
          } else {
            // Empty-cart path — beginAdd's synthetic wrapper sits inside
            // #CartDrawer-CartItems. Replace it with the server-rendered
            // one.
            const syntheticWrapper = drawer.querySelector("[data-skeleton-wrapper]");
            if (syntheticWrapper) {
              syntheticWrapper.replaceWith(newWrapper);
            } else {
              const container = drawer.querySelector("#CartDrawer-CartItems");
              if (container) container.appendChild(newWrapper);
            }
          }
        }
      } catch (e) {
        Logger.warn("[FastAdd] items section swap failed:", e);
        // Fall through to the legacy client renderer below.
      }
    }

    // Legacy / fallback path: no items section in the response (e.g.
    // interceptor didn't run, or response shape changed). Fall back to
    // the client-side cart-item renderer + quantity-merge guard.
    if (!itemsSectionHtml) {
      if (hasMerge) {
        if (typeof surgicalRefreshCartDrawer === "function") {
          surgicalRefreshCartDrawer();
        } else if (typeof refreshCartDrawerWithReinit === "function") {
          refreshCartDrawerWithReinit();
        }
        this._fastAddPending = false;
        return true;
      }

      const skeletonRows = drawer.querySelectorAll("[data-skeleton]");
      const existingRealItems = drawer.querySelectorAll(".cart-item:not([data-skeleton])");
      const startIndex = existingRealItems.length + 1;
      const newLis = [];
      for (let i = 0; i < items.length; i++) {
        const li = this._renderCartItemLi(items[i], startIndex + i);
        if (!li) return false;
        newLis.push(li);
      }

      let list =
        drawer.querySelector("ul.cart-items[data-skeleton-list]") ||
        drawer.querySelector("ul.cart-items");
      if (!list) return false;

      for (let i = 0; i < newLis.length; i++) {
        const skel = skeletonRows[i];
        if (skel && skel.parentNode === list) {
          list.replaceChild(newLis[i], skel);
        } else {
          list.insertBefore(newLis[i], list.firstElementChild);
        }
      }
      for (let i = newLis.length; i < skeletonRows.length; i++) {
        if (skeletonRows[i] && skeletonRows[i].parentNode) {
          skeletonRows[i].parentNode.removeChild(skeletonRows[i]);
        }
      }
      if (list.hasAttribute("data-skeleton-list")) {
        list.removeAttribute("data-skeleton-list");
        const wrapper = list.closest("[data-skeleton-wrapper]");
        if (wrapper) wrapper.removeAttribute("data-skeleton-wrapper");
      }
    }

    // Swap cart-icon-bubble from the response (small section, server-
    // rendered, authoritative for the bubble count).
    const bubbleHtml = parsedState.sections && parsedState.sections["cart-icon-bubble"];
    if (bubbleHtml) {
      const bubble = document.getElementById("cart-icon-bubble");
      if (bubble) {
        try {
          const tmp = new DOMParser().parseFromString(bubbleHtml, "text/html");
          const inner = tmp.querySelector(".shopify-section") || tmp.body.firstElementChild;
          if (inner) bubble.innerHTML = inner.innerHTML;
        } catch (_) {}
      }
    }

    // Update #cart-drawer-state JSON tag so the in-DOM source of truth
    // matches the new cart. The element was rendered with pre-add values
    // since we skipped the cart-drawer section render. Compute the new
    // totals from lastCart + the added items.
    const addedCount = items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
    const addedPrice = items.reduce((n, it) => n + (Number(it.line_price) || Number(it.final_line_price) || 0), 0);
    const prevCount = (this.lastCart && this.lastCart.item_count) || 0;
    const prevTotal = (this.lastCart && this.lastCart.total_price) || 0;
    const newCount = prevCount + addedCount;
    const newTotal = prevTotal + addedPrice;
    const stateEl = document.getElementById("cart-drawer-state");
    let currency = "AUD";
    let prevTotalDiscount = (this.lastCart && this.lastCart.total_discount) || 0;
    if (stateEl) {
      try {
        const cur = JSON.parse(stateEl.textContent);
        currency = cur.currency || currency;
        if (typeof cur.total_discount === "number") prevTotalDiscount = cur.total_discount;
        stateEl.textContent = JSON.stringify({
          item_count: newCount,
          total_price: newTotal,
          // Preserve the previous total_discount — fast-add can't predict
          // the new total without server-side cart rule evaluation.
          total_discount: prevTotalDiscount,
          currency,
        });
      } catch (_) {}
    }

    // Drive progress bar / item counter / state from the merged cart.
    // Strip the boot-stub flag (we have real item_count + total_price now),
    // but flag items[] as stale: we copied items from previous lastCart
    // and didn't include the new add. Gift managers must skip until
    // surgicalRefreshCartDrawer's parallel /cart.js fetch repopulates
    // items[] — otherwise MysteryGiftManager (which counts
    // items.filter(non-mystery)) could wrongly remove a gift when the
    // cart had only the gift before the add.
    const mergedCart = {
      ...((this.lastCart) || {}),
      item_count: newCount,
      total_price: newTotal,
      currency,
      isStub: false,
      _itemsStale: true,
    };
    this.updateState(mergedCart);

    // Footer subtotal — update inline so the user sees the right price
    // before the background section refresh lands.
    const subtotalEl = document.querySelector(".totals__subtotal-value");
    if (subtotalEl) {
      const SH = window.Shopify;
      const moneyFmt =
        (SH && SH.money_format) ||
        (window.theme && window.theme.moneyFormat) ||
        null;
      let display;
      if (SH && typeof SH.formatMoney === "function" && moneyFmt) {
        try { display = SH.formatMoney(newTotal, moneyFmt); } catch (_) {}
      }
      if (!display) display = "$" + (newTotal / 100).toFixed(2);
      // Liquid renders with a leading space in `{{- cart.total_price | money_with_currency }}`.
      subtotalEl.textContent = " " + display;
    }

    // Schedule a surgical background refresh to reconcile the items list
    // and totals with server-rendered HTML. Surgical (not full-innerHTML)
    // because the chrome — timer, progress bar, announcement bar, footer
    // CTAs — was already correctly updated client-side and a full swap
    // would needlessly destroy/recreate those nodes (and risk a flicker
    // between our client-rendered LIs and the server-rendered ones).
    if (typeof surgicalRefreshCartDrawer === "function") {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 600));
      schedule(() => {
        try { surgicalRefreshCartDrawer(); } catch (_) {}
      });
    }

    this._fastAddPending = false;
    return true;
  }

  /**
   * Update state from cart data - called by cart.js BEFORE sections are replaced
   * This ensures MutationObserver applies the NEW state when DOM changes
   */
  updateState(cart) {
    // Optimistic window closed — onCartOpen() can resume fetching /cart.js now.
    this._optimisticAddInFlight = false;
    clearTimeout(this._optimisticAddTimeout);

    if (!cart) return;

    // Defensive: /cart/add.js with sections= returns line-item shape
    // ({items: [...], sections: {...}}, no item_count). Don't apply this
    // as cart state — calculateState would read item_count as undefined,
    // flip isVisible to false, and hide the progress bar. CartWatcher's
    // /cart.js will provide the real cart shortly.
    if (typeof cart.item_count !== "number") {
      Logger.warn("[CartEnhancements] updateState skipped — missing item_count");
      return;
    }

    Logger.log("[CartEnhancements] updateState:", {
      itemCount: cart.item_count,
      total: (cart.total_price / 100).toFixed(2),
    });

    // Store this cart data
    this.lastCart = cart;
    this._lastCartFetchTime = Date.now();

    // Update UI components immediately (no cart modifications)
    this.progressBar.update(cart);
    this.itemCounter.update(cart);

    // Eagerly check gift eligibility from cart.js response.
    // Only safe when the items array matches item_count — when cart was
    // merged from a partial /cart/add.js response, items is stale (still
    // shows pre-add line items) so the check would mis-detect whether a
    // free gift is present and could add a duplicate or remove a real
    // one. CartWatcher's /cart.js fires ~200–300ms later with the full
    // cart and will run the check then.
    const itemsMatchCount =
      Array.isArray(cart.items) &&
      cart.items.reduce((sum, it) => sum + (it.quantity || 0), 0) === cart.item_count;
    if (
      itemsMatchCount &&
      cart.item_count > 0 &&
      !this.freeGiftManager.isProcessing
    ) {
      const timeSinceOp =
        Date.now() - (this.freeGiftManager.lastOperationTime || 0);
      if (timeSinceOp > 1500) {
        this.freeGiftManager.checkAndManage(cart);
      }
    }
  }

  /**
   * Optimistic open: called by product-form, sticky-add-to-cart, and
   * bundle-picker BEFORE their /cart/add.js fetch. Opens the drawer
   * immediately and appends N skeleton rows so the user sees progress
   * during the ~500–1500ms server round-trip. Skeletons are removed
   * automatically when renderContents() swaps the drawer HTML.
   *
   * Behaviour:
   * - If the drawer is empty (no .cart-items <ul>), still open the
   *   drawer but skip skeleton injection — the empty-state markup
   *   replaces the items list and the response render flips it.
   * - If the drawer is already open and showing skeletons from a
   *   previous click, that's fine — they'll all get wiped together.
   *
   * @param {number} quantity - number of skeleton rows to append (default 1)
   */
  /**
   * Populate a CartDrawer-PreviewItem clone with product data. Mirrors
   * the structure cart-drawer-items.liquid renders for a real cart
   * item: colour swatch + size text + price-with-currency.
   *
   * @param {HTMLElement} node - cloned <li> from CartDrawer-PreviewItem template
   * @param {Object} data
   * @param {string} data.title
   * @param {string} data.image
   * @param {string} data.variant_title - fallback when options[] absent
   * @param {Array<{name:string, value:string}>} [data.options] - product options paired with values
   * @param {number} data.price - cents
   */
  _populatePreview(node, data) {
    if (!node || !data) return;

    // Image — size the CDN URL up so the ~80px box stays crisp on 2x/3x
    // displays (raw product image URLs often arrive at a small width).
    const img = node.querySelector("[data-preview-image]");
    if (img && data.image) {
      const sized = (raw, w) => {
        try {
          const u = new URL(raw, window.location.origin);
          u.searchParams.set("width", String(w));
          return u.toString();
        } catch (_) {
          return raw;
        }
      };
      img.src = sized(data.image, 240);
      img.srcset = [80, 120, 160, 240]
        .map((w) => `${sized(data.image, w)} ${w}w`)
        .join(", ");
      img.sizes = "80px";
      if (data.title) img.alt = data.title;
    }

    // Title — caller is expected to extract from the inner <h1>, not the
    // outer wrapper (avoids the duplicated-title issue from
    // `.product__title` containing both the real h1 and a nested link).
    const title = node.querySelector("[data-preview-title]");
    if (title) title.textContent = data.title || "";

    // Colour swatch + variant title — mirror cart-drawer-items.liquid's
    // pattern: <div.cart-item__product-options><div.cart-item__variant-option>
    // <div.cart-item__option-swatch data-color style="background:..."></div>
    // </div></div> followed by <div.cart-item__variant-title>Size: X</div>.
    const swatchHost = node.querySelector("[data-preview-options]");
    const variantTitleEl = node.querySelector("[data-preview-variant]");
    const options = Array.isArray(data.options) ? data.options : null;
    if (options && swatchHost && variantTitleEl) {
      const isColourName = (n) => {
        const v = String(n || "").toLowerCase();
        return v === "color" || v === "colour";
      };
      const colourOpt = options.find((o) => o && isColourName(o.name));
      const otherOpts = options.filter((o) => o && !isColourName(o.name) && !String(o.value || "").toLowerCase().includes("bundle"));

      if (colourOpt && colourOpt.value) {
        // Try to steal the styled swatch off the PDP — that has the
        // correct background-image + background-color baked in by Liquid
        // for the colour metaobject. Fall back to background-color: <name>.
        const valHandle = String(colourOpt.value).toLowerCase()
          .replace(/&amp;/g, "-").replace(/-and-/g, "-")
          .replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-")
          .replace(/^-|-$/g, "");
        const pdpSwatch =
          document.querySelector(`[data-swatch-color="${valHandle}"]`) ||
          document.querySelector(`[data-color="${colourOpt.value}"]`);
        const swatchStyle = pdpSwatch
          ? pdpSwatch.getAttribute("style") || ""
          : `background-color: ${colourOpt.value}`;
        const swatchWrap = document.createElement("div");
        swatchWrap.className = "cart-item__variant-option";
        const swatch = document.createElement("div");
        swatch.className = "cart-item__option-swatch color-container";
        swatch.setAttribute("data-color", colourOpt.value);
        swatch.setAttribute("style", swatchStyle);
        swatchWrap.appendChild(swatch);
        swatchHost.appendChild(swatchWrap);
        swatchHost.removeAttribute("hidden");
      }

      if (otherOpts.length) {
        variantTitleEl.textContent = otherOpts
          .map((o) => `${o.name}: ${o.value}`)
          .join(", ");
        variantTitleEl.removeAttribute("hidden");
      }
    } else if (variantTitleEl && data.variant_title && data.variant_title !== "Default Title") {
      // No structured options provided — fall back to the raw variant title.
      variantTitleEl.textContent = data.variant_title;
      variantTitleEl.removeAttribute("hidden");
    }

    // Price — match Liquid's `money_with_currency` filter (shows "$X.YY AUD")
    // by preferring Shopify.money_with_currency_format / theme.moneyFormatWithCurrency,
    // with a fallback that appends the active currency code so the
    // preview reads "$22.50 AUD" not "$22.50".
    const price = node.querySelector("[data-preview-price]");
    if (price && typeof data.price === "number") {
      const SH = window.Shopify;
      const moneyFmt =
        (SH && SH.money_with_currency_format) ||
        (window.theme && window.theme.moneyWithCurrencyFormat) ||
        (SH && SH.money_format) ||
        (window.theme && window.theme.moneyFormat) ||
        null;
      // Currency code: prefer the cart-drawer-state JSON (set from
      // cart.currency.iso_code on every section swap), fall back to
      // Shopify.currency.active, then AUD.
      let currencyCode = "AUD";
      try {
        const stateEl = document.getElementById("cart-drawer-state");
        if (stateEl) {
          const parsed = JSON.parse(stateEl.textContent);
          if (parsed && parsed.currency) currencyCode = parsed.currency;
        } else if (SH && SH.currency && SH.currency.active) {
          currencyCode = SH.currency.active;
        }
      } catch (_) {}
      const fmt = (cents) => {
        if (SH && typeof SH.formatMoney === "function" && moneyFmt) {
          try { return SH.formatMoney(cents, moneyFmt); } catch (_) {}
        }
        return "$" + (cents / 100).toFixed(2) + " " + currencyCode;
      };
      // Bundle / sale path: when caller supplied original_price and it
      // exceeds the (already-discounted) price, render the same sale +
      // strikethrough markup as cart-drawer-items.liquid does for an
      // automatic line-level discount. The price slot is the regular
      // <span class="cart-item__price-regular"> — swap it for the
      // two-span sale layout when applicable.
      if (typeof data.original_price === "number" && data.original_price > data.price) {
        const wrapper = price.parentElement;
        if (wrapper) {
          wrapper.innerHTML =
            `<span class="cart-item__price-sale">${fmt(data.price)}</span>` +
            `<span class="cart-item__price-compare-at"><s>${fmt(data.original_price)}</s></span>`;
        } else {
          price.textContent = fmt(data.price);
        }
      } else {
        price.textContent = fmt(data.price);
      }
    } else if (price) {
      price.remove();
    }
  }

  beginAdd(quantity = 1, items = null) {
    try {
      const drawer = this.cartDrawer || document.querySelector("cart-drawer");
      if (!drawer) return;

      // Bump the cart-icon-bubble count optimistically. The bubble
      // section render on the response will overwrite this with truth.
      const countEl = document.getElementById("cart-item-count");
      if (countEl) {
        const current = parseInt(countEl.textContent, 10) || 0;
        countEl.textContent = String(current + quantity);
      }
      const bubble = document.querySelector("#cart-icon-bubble .cart-count-bubble span:not(.visually-hidden)");
      if (bubble) {
        const current = parseInt(bubble.textContent, 10) || 0;
        bubble.textContent = String(current + quantity);
      }

      // Decide preview vs skeleton: when the caller supplied per-item
      // data ({title, image, variant_title, price}) and the count
      // matches, render a populated preview that mirrors the real
      // cart-item layout. Otherwise fall back to the generic shimmer.
      const usePreview =
        Array.isArray(items) &&
        items.length === quantity &&
        document.getElementById("CartDrawer-PreviewItem") !== null;
      const template = document.getElementById(
        usePreview ? "CartDrawer-PreviewItem" : "CartDrawer-SkeletonItem",
      );
      let list = drawer.querySelector("ul.cart-items");

      // Empty cart: Liquid didn't render the <ul.cart-items> at all.
      // Synthesise it inside #CartDrawer-CartItems, hide the empty-state
      // block, and flip is-empty so the drawer layout switches from
      // centred-empty grid to the normal scrollable items column.
      if (!list && template && template.content) {
        const container = drawer.querySelector("#CartDrawer-CartItems");
        if (container) {
          // Match the populated-cart markup: <div.drawer__cart-items-wrapper><ul.cart-items>.
          // The wrapper carries `flex: 1; overflow-y: auto` so the synthetic
          // list participates in the same scroll/flex layout as the real
          // items list. Without it the synthetic ul collapses inside a
          // flex parent and the skeleton can render zero-height.
          const wrapper = document.createElement("div");
          wrapper.className = "drawer__cart-items-wrapper";
          wrapper.setAttribute("data-skeleton-wrapper", "true");
          list = document.createElement("ul");
          list.className = "cart-items";
          list.setAttribute("role", "list");
          list.setAttribute("data-skeleton-list", "true");
          wrapper.appendChild(list);
          container.appendChild(wrapper);

          const emptyBlock = drawer.querySelector(".drawer__inner-empty");
          if (emptyBlock && emptyBlock.style.display !== "none") {
            // [hidden] doesn't beat `.drawer__inner-empty { display: flex }`,
            // so force-hide inline and record what we did for rollback.
            emptyBlock.setAttribute("data-skeleton-prev-display", emptyBlock.style.display || "");
            emptyBlock.style.display = "none";
          }
          if (drawer.classList.contains("is-empty")) {
            drawer.classList.remove("is-empty");
            drawer.setAttribute("data-skeleton-was-empty", "true");
          }

          // <cart-drawer-items class="is-empty"> hides the footer (via
          // `cart-drawer-items.is-empty + .drawer__footer { display:none }`).
          // Removing it now matches the final layout and stops the footer
          // popping in when the section swap lands. Tag with the class
          // name to restore on rollback (sibling progress/timer use
          // `is-cart-empty`, so we can't share a marker).
          const drawerItems = drawer.querySelector("cart-drawer-items");
          if (drawerItems && drawerItems.classList.contains("is-empty")) {
            drawerItems.classList.remove("is-empty");
            drawerItems.setAttribute("data-skeleton-was-class", "is-empty");
          }

          // Expand timer header + progress section that Liquid rendered
          // collapsed (.is-cart-empty). Otherwise they pop in on the
          // response, shoving the skeleton down — a noticeable layout
          // shift. Letting them slide open during the ~500ms wait
          // matches the final layout.
          const progressSection = drawer.querySelector("#cart-drawer-progress-section");
          const timerHeader = drawer.querySelector("#cart-drawer-timer-header");
          [progressSection, timerHeader].forEach((el) => {
            if (el && el.classList.contains("is-cart-empty")) {
              el.classList.remove("is-cart-empty");
              el.setAttribute("data-skeleton-was-empty", "true");
            }
          });
        }
      }

      if (list && template && template.content) {
        for (let i = 0; i < quantity; i++) {
          const node = template.content.firstElementChild.cloneNode(true);
          if (usePreview) this._populatePreview(node, items[i]);
          list.insertBefore(node, list.firstElementChild);
        }
      }

      // Flag the optimistic window. The monkey-patched cart-drawer.open()
      // schedules onCartOpen() ~300ms after open — which would fire a
      // fresh /cart.js while /cart/add.js is still in flight, return the
      // pre-add cart (item_count = 0), and clobber our optimistic count.
      // onCartOpen() reads this flag and bails. Cleared by updateState
      // when the real response lands, by rollbackOptimistic on failure,
      // or by the safety timeout if something else swallows both.
      this._optimisticAddInFlight = true;
      clearTimeout(this._optimisticAddTimeout);
      this._optimisticAddTimeout = setTimeout(() => {
        this._optimisticAddInFlight = false;
      }, 5000);

      // Tell CartWatcher to skip its emit for the next /cart/add — the
      // caller (product-form / sticky-add-to-cart / bundle-picker) is
      // about to fire it and renderContents → updateState will handle
      // the UI. Otherwise we get a redundant onCartUpdate ~1s after the
      // drawer is already populated. CartWatcher still refreshes its
      // localStorage so external mutations diff correctly.
      if (this.cartWatcher) {
        this.cartWatcher.skipNextAdd = true;
      }

      // Open the drawer if not already. open() is idempotent if active.
      if (typeof drawer.open === "function" && !drawer.classList.contains("active")) {
        drawer.open();
      }
    } catch (e) {
      Logger.warn("[CartEnhancements] beginAdd failed:", e);
    }
  }

  /**
   * Roll back the optimistic state from a failed beginAdd: remove any
   * skeleton rows from the drawer and reset the cart-icon-bubble count
   * to the last known authoritative cart. Callers fire this in their
   * fetch error / non-2xx branch.
   */
  rollbackOptimistic() {
    this._optimisticAddInFlight = false;
    clearTimeout(this._optimisticAddTimeout);
    try {
      const drawer = this.cartDrawer || document.querySelector("cart-drawer");
      if (drawer) {
        drawer.querySelectorAll("[data-skeleton]").forEach((el) => el.remove());
        // Remove synthetic wrapper + list (created by the empty-cart
        // branch). Wrapper first so the list comes with it.
        const syntheticWrapper = drawer.querySelector("[data-skeleton-wrapper]");
        if (syntheticWrapper) syntheticWrapper.remove();
        const syntheticList = drawer.querySelector("[data-skeleton-list]");
        if (syntheticList) syntheticList.remove();
        if (drawer.hasAttribute("data-skeleton-was-empty")) {
          drawer.classList.add("is-empty");
          drawer.removeAttribute("data-skeleton-was-empty");
        }
        drawer.querySelectorAll("[data-skeleton-was-empty]").forEach((el) => {
          el.classList.add("is-cart-empty");
          el.removeAttribute("data-skeleton-was-empty");
        });
        // Elements with a specific class to restore (e.g. cart-drawer-items
        // → is-empty). data-skeleton-was-empty above carries the implicit
        // is-cart-empty semantic for progress/timer headers.
        drawer.querySelectorAll("[data-skeleton-was-class]").forEach((el) => {
          const cls = el.getAttribute("data-skeleton-was-class");
          if (cls) el.classList.add(cls);
          el.removeAttribute("data-skeleton-was-class");
        });
        const hiddenEmptyBlock = drawer.querySelector(".drawer__inner-empty[data-skeleton-prev-display]");
        if (hiddenEmptyBlock) {
          hiddenEmptyBlock.style.display = hiddenEmptyBlock.getAttribute("data-skeleton-prev-display") || "";
          hiddenEmptyBlock.removeAttribute("data-skeleton-prev-display");
        }
      }
      // Failed add — clear the CartWatcher suppression too.
      if (this.cartWatcher) {
        this.cartWatcher.skipNextAdd = false;
      }
      const truth = this.lastCart?.item_count;
      if (typeof truth === "number") {
        const countEl = document.getElementById("cart-item-count");
        if (countEl) countEl.textContent = String(truth);
        const bubble = document.querySelector(
          "#cart-icon-bubble .cart-count-bubble span:not(.visually-hidden)",
        );
        if (bubble) bubble.textContent = String(truth);
      }
    } catch (e) {
      Logger.warn("[CartEnhancements] rollbackOptimistic failed:", e);
    }
  }

  /**
   * Warm the HTTP cache for /cart sections on hover/focus of the cart
   * icon or any add-to-cart button. Fires once per page — repeat
   * triggers no-op so we don't pile up requests on a long PDP visit.
   * Doesn't help the add-response itself (server renders that fresh
   * post-mutation), but means a subsequent drawer-only open or
   * post-error refresh comes back instantly.
   */
  _prefetchCartSections() {
    if (this._cartSectionsPrefetched) return;
    this._cartSectionsPrefetched = true;
    try {
      const fetchFn = window._originalFetch || window.fetch;
      const url = CART_DRAWER_SECTIONS_URL;
      fetchFn(url, { priority: "low", credentials: "same-origin" }).catch(() => {
        // Reset flag so a future hover can retry on transient failure.
        this._cartSectionsPrefetched = false;
      });
    } catch (_) {
      this._cartSectionsPrefetched = false;
    }
  }

  _wireHoverPrefetch() {
    const prefetch = () => this._prefetchCartSections();
    const opts = { once: true, passive: true, capture: true };

    // Cart icon: every page has this in the header.
    const cartIcon = document.getElementById("cart-icon-bubble");
    if (cartIcon) {
      cartIcon.addEventListener("pointerenter", prefetch, opts);
      cartIcon.addEventListener("focusin", prefetch, opts);
    }

    // Add-to-cart buttons: delegate at the document level since the
    // PDP/quick-add can rewire forms after load. Capture phase + once
    // means at most one listener fires across the whole page lifetime.
    const delegated = (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== "function") return;
      if (target.closest('product-form, sticky-add-to-cart, [data-bundle-submit]')) {
        prefetch();
      }
    };
    document.addEventListener("pointerenter", delegated, { capture: true, passive: true, once: true });
    document.addEventListener("focusin", delegated, { capture: true, passive: true, once: true });
  }

  setup() {
    this.cartDrawer = document.querySelector("cart-drawer");
    if (!this.cartDrawer) {
      setTimeout(() => this.setup(), 100);
      return;
    }

    this._wireHoverPrefetch();

    // On back/forward navigation, the browser may serve a bfcache page
    // with a stale cart drawer and count. Only fire the sync when the
    // page actually came from bfcache — on a fresh load, the server
    // already rendered fresh sections so this would be a redundant
    // 2-section Liquid render.
    window.addEventListener("pageshow", async (event) => {
      if (!event.persisted) return;
      try {
        const fetchFn = window._originalFetch || window.fetch;
        const resp = await fetchFn(
          CART_DRAWER_SECTIONS_URL,
        );
        if (!resp.ok) return;
        const data = await resp.json();

        // Update cart icon bubble
        const cartBubble = document.getElementById("cart-icon-bubble");
        if (cartBubble && data["cart-icon-bubble"]) {
          const tmp = document.createElement("div");
          tmp.innerHTML = data["cart-icon-bubble"];
          const content =
            tmp.querySelector(".shopify-section") || tmp.firstElementChild;
          if (content) cartBubble.innerHTML = content.innerHTML;
        }

        // Update cart drawer to prevent stale/empty drawer after back navigation
        if (data["cart-drawer"] && this.cartDrawer) {
          const tmpDrawer = document.createElement("div");
          tmpDrawer.innerHTML = data["cart-drawer"];
          const newSection =
            tmpDrawer.querySelector("cart-drawer") || tmpDrawer.firstElementChild;
          if (newSection) {
            const wasActive = this.cartDrawer.classList.contains("active");
            this.cartDrawer.innerHTML = newSection.innerHTML;
            if (wasActive) {
              this.cartDrawer.classList.add("active", "animate");
            }
            const hasItems = this.cartDrawer.querySelector(".cart-item") !== null;
            this.cartDrawer.classList.toggle("is-empty", !hasItems);
            const overlay = this.cartDrawer.querySelector("#CartDrawer-Overlay");
            if (overlay) {
              overlay.addEventListener("click", () => this.cartDrawer.close());
            }
          }
        }
      } catch (e) {
        // Silently fail — the drawer will sync on next cart interaction
      }
    });

    // Initialize CartWatcher — this fetches /cart.js once to seed
    // localStorage and starts the PerformanceObserver. We reuse that
    // cart for the initial UI hydration (see fetchAndUpdateCart below),
    // avoiding a second /cart.js on every page load.
    this._cartWatcherBoot = this.cartWatcher.init();

    // Listen for cart_changed events from CartWatcher
    // This is much cleaner than intercepting fetch calls!
    this.lastCartWatcherEventTime = 0;
    window.addEventListener("cart_changed", (e) => {
      if (e.detail && e.detail.newCart) {
        const timestamp = Date.now();
        const newCart = e.detail.newCart;
        const changes = e.detail.changes || { added: [], removed: [] };

        // Check if we just completed a free gift operation (ignore our own changes)
        const timeSinceFreeGiftOp =
          timestamp - (this.freeGiftManager.lastOperationTime || 0);
        // Check by _free_gift property only (not handle) so gift product can be changed
        const freeGiftAdded = changes.added?.some(
          (item) => item.properties?._free_gift === "true",
        );
        const freeGiftRemoved = changes.removed?.some(
          (item) => item.properties?._free_gift === "true",
        );

        // If we just added/removed the gift ourselves, update data components but skip gift re-check
        if ((freeGiftAdded || freeGiftRemoved) && timeSinceFreeGiftOp < 2000) {
          Logger.log(
            `[CartEnhancements] Gift change from our operation - updating UI only`,
          );
          this.progressBar.update(newCart);
          this.itemCounter.update(newCart);
          // Refresh sections to ensure cart drawer reflects the change
          const currentItemCount =
            this.cartDrawer?.querySelectorAll(".cart-item").length || 0;
          if (Math.abs(currentItemCount - newCart.item_count) > 0) {
            refreshCartDrawerWithReinit(newCart.item_count);
          }
          return;
        }

        this.lastCartWatcherEventTime = timestamp;

        // Get triggering request info from event detail
        const triggeringRequest = e.detail.triggeringRequest;

        Logger.log("[CartEnhancements] cart_changed event received:", {
          added: changes.added?.length || 0,
          removed: changes.removed?.length || 0,
          newItemCount: newCart.item_count,
        });

        // Debounce updates - cancel pending and schedule new one
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
        }

        this.pendingUpdate = setTimeout(async () => {
          this.pendingUpdate = null;

          // Update UI components immediately (no fetch needed)
          this.progressBar.update(newCart);
          this.itemCounter.update(newCart);

          // Handle gift logic FIRST (the check itself is fast, no network needed)
          await this.onCartUpdate(newCart);

          // Then refresh cart drawer HTML if item count changed
          // Skip if a gift operation just happened (it handles its own section refresh)
          const recentGiftOp =
            Date.now() - (this.freeGiftManager.lastOperationTime || 0) < 2000;
          if (!recentGiftOp) {
            const currentItemCount =
              this.cartDrawer?.querySelectorAll(".cart-item").length || 0;
            if (Math.abs(currentItemCount - newCart.item_count) > 0) {
              refreshCartDrawerWithReinit(newCart.item_count);
            }
          }
        }, this.UPDATE_DEBOUNCE);
      }
    });

    // Listen for legacy cart:updated events (from other parts of the theme)
    window.addEventListener("cart:updated", (e) => {
      if (e.detail && e.detail.cart) {
        setTimeout(() => this.onCartUpdate(e.detail.cart), 100);
      }
    });

    // Try to override cart drawer open method if it exists
    if (this.cartDrawer.open && typeof this.cartDrawer.open === "function") {
      const originalOpen = this.cartDrawer.open.bind(this.cartDrawer);
      this.cartDrawer.open = (triggeredBy) => {
        originalOpen(triggeredBy);
        setTimeout(() => this.onCartOpen(), 300);
      };
    }

    // (renderContents fast-add patch is installed at the prototype level
    // in init() — see _patchCartDrawerRenderContents.)
    //
    // No MutationObserver on the drawer's class attribute: the patched
    // open() above is the sole entry point that should trigger
    // onCartOpen, and the four other call sites that set the active
    // class (refreshCartDrawerWithReinit, the pageshow restoration, the
    // fast-add render's setTimeout, and the bundle path) are all
    // restoring state after an innerHTML wipe — they intentionally
    // shouldn't fire onCartOpen. The previous observer used a
    // `lastActive` guard to skip those, which made it dead weight in
    // practice while still running a callback on every class mutation
    // (is-empty toggles, animate adds, etc.). onCartOpen has its own
    // _lastOpenTime debounce as a safety net against accidental double
    // invocations from anywhere else.

    // Fetch cart data once on initial load
    this.fetchAndUpdateCart();
  }

  async fetchAndUpdateCart() {
    try {
      // Reuse the cart fetched by CartWatcher.init() if available —
      // avoids a duplicate /cart.js on every page load.
      let cart = null;
      if (this._cartWatcherBoot) {
        try {
          cart = await this._cartWatcherBoot;
        } catch (_) {
          cart = null;
        }
      }
      if (!cart) {
        const fetchFn = window._originalFetch || window.fetch;
        const response = await fetchFn(CART_JS_URL);
        if (!response.ok) return;
        cart = await response.json();
      }
      this.lastCart = cart;
      this._lastCartFetchTime = Date.now();

      if (cart.isStub) {
        // Boot stub from Liquid #cart-drawer-state — has counts/totals
        // but no items[]. Run the lightweight UI updates immediately so
        // the progress bar / bubble / timer reflect the cart from the
        // first paint, then defer the real /cart.js fetch for gift
        // management (which needs items[] to decide what to add/remove).
        this.progressBar.update(cart);
        this.itemCounter.update(cart);
        if (this.timer && cart.item_count > 0) {
          try { this.timer.init && this.timer.init(); } catch (_) {}
        }
        const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
        schedule(async () => {
          try {
            const fetchFn = window._originalFetch || window.fetch;
            const response = await fetchFn(CART_JS_URL);
            if (!response.ok) return;
            const realCart = await response.json();
            // Skip if the user has clicked ATC in the meantime — this
            // deferred fetch races the in-flight /cart/add.js and would
            // return the PRE-add cart, overwriting the bumped counter
            // and the optimistic preview row with empty state.
            if (this._optimisticAddInFlight) return;
            this.lastCart = realCart;
            this._lastCartFetchTime = Date.now();
            if (this.cartWatcher && typeof this.cartWatcher.storeCart === "function") {
              this.cartWatcher.storeCart(realCart);
            }
            await this.updateAll(realCart);
          } catch (e) {
            Logger.warn("[Boot] deferred /cart.js failed:", e);
          }
        });
        return;
      }

      await this.updateAll(cart);
    } catch (error) {
      Logger.error("Error fetching cart:", error);
    }
  }

  async onCartOpen() {
    // Skip during an optimistic add window. Fetching /cart.js here can
    // race the in-flight /cart/add.js and return the pre-add cart,
    // briefly clobbering the optimistic count back to its old value
    // (e.g. 1 → 0 → 1 on the first add, 4 → 0 → 4 on a bundle).
    if (this._optimisticAddInFlight) {
      return;
    }

    // Debounce rapid open events (can happen during DOM refresh)
    if (this._lastOpenTime && Date.now() - this._lastOpenTime < 500) {
      return;
    }
    this._lastOpenTime = Date.now();

    // If a recent cart fetch is already in-memory, reuse it. The cart was
    // typically just fetched by pageshow, fetchAndUpdateCart, cart.js (via
    // updateState), or CartWatcher's cart_changed flow — all of which
    // timestamp _lastCartFetchTime. Skipping this fetch removes one
    // round-trip on every drawer open.
    const cartAge = Date.now() - (this._lastCartFetchTime || 0);
    if (this.lastCart && cartAge < 1000) {
      if (this.lastCart.item_count > 0) this.timer.init();
      this.updateAll(this.lastCart);
      return;
    }

    try {
      const fetchFn = window._originalFetch || window.fetch;
      const response = await fetchFn(CART_JS_URL);
      if (response.ok) {
        const cart = await response.json();
        this.lastCart = cart;
        this._lastCartFetchTime = Date.now();

        // Only initialize timer if cart has items
        if (cart && cart.item_count > 0) {
          this.timer.init();
        }

        this.updateAll(cart);
      }
    } catch (error) {
      Logger.error("Error fetching cart:", error);
      // Still try to update with default currency
      this.progressBar.update({
        currency: "AUD",
        item_count: 0,
        items: [],
        total_price: 0,
      });
    }
  }

  async onCartUpdate(cart) {
    const timestamp = Date.now();

    // Skip if we just processed a free gift change (avoid loops)
    const timeSinceFreeGiftOp =
      timestamp - (this.freeGiftManager.lastOperationTime || 0);
    if (timeSinceFreeGiftOp < 1500) {
      Logger.log(
        `[CartEnhancements] Skipping gift check - operation ${timeSinceFreeGiftOp}ms ago`,
      );
      // Still update UI
      this.progressBar.update(cart);
      this.itemCounter.update(cart);
      return;
    }

    Logger.log(`[CartEnhancements] onCartUpdate:`, {
      itemCount: cart?.item_count,
      total: cart?.total_price ? (cart.total_price / 100).toFixed(2) : "0",
    });

    this.lastCart = cart;
    this._lastCartFetchTime = Date.now();
    await this.updateAll(cart);
  }

  async updateAll(cart) {
    // Handle timer based on cart state
    if (!cart || cart.item_count === 0) {
      this.timer.stop();
    } else {
      if (!this.timer.isRunning) {
        this.timer.init();
      }
    }

    // Update progress bar (state-based, efficient)
    this.progressBar.update(cart);

    // Update item counter (lightweight)
    this.itemCounter.update(cart);

    // Gift managers need real items[] to decide what to add/remove.
    // Skip when:
    //   - cart is the boot stub (Liquid state, items[] = [])
    //   - items[] is stale after a fast-add (item_count/total_price are
    //     correct but items[] is the pre-add list)
    // Both are repaired by a subsequent /cart.js fetch (boot:
    // fetchAndUpdateCart deferred call; fast-add: surgicalRefreshCartDrawer)
    // that re-invokes updateAll with a real cart, at which point gift
    // checks run.
    if (cart && (cart.isStub || cart._itemsStale)) return;

    // Check and manage free gift (has internal debouncing)
    if (cart && cart.item_count > 0 && !this.freeGiftManager.isProcessing) {
      await this.freeGiftManager.checkAndManage(cart);
    }

    // Check and manage mystery gift
    if (
      !this.mysteryGiftManager.isAddingToCart &&
      !this.mysteryGiftManager.isRemovingFromCart
    ) {
      await this.mysteryGiftManager.checkAndManage(cart);
    }
  }
}

// Initialize the enhancements
window.cartDrawerEnhancements = new CartDrawerEnhancements();
